import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  MinusIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Plot from "react-plotly.js";

import UserContext from "context/UserContext";

import Card from "components/atoms/Card";
import Spinner from "components/atoms/Spinner";

import { error, success } from "utils";

// Available AI model info from /models endpoint
interface AvailableModel {
  tag: string;
  name: string;
  token_multiplier: number;
  image_multiplier?: number;
  supports_multimodal: boolean;
}

// AI Insights types - properly typed for the EasyAI response
interface AIInsightsAlert {
  severity: "critical" | "warning" | "info";
  title: string;
  message: string;
  context?: string;
}

interface AIInsightsKeyInsight {
  name: string;
  value: string;
  description: string;
}

interface AIInsightsRecommendation {
  type:
    | "cost_savings"
    | "accuracy_boost"
    | "feature_discovery"
    | "model_upgrade"
    | "model_downgrade";
  priority: "high" | "medium" | "low";
  route_key: string;
  route_name: string;
  title: string;
  description: string;
  estimated_impact: string;
  reasoning: string;
  current_model?: string;
  suggested_model?: string;
}

interface AIInsightsScore {
  name: string;
  value: number;
  max_value: number;
  description: string;
}

interface AIInsightsUnusedFeature {
  route_key: string;
  route_name: string;
  category: string;
  value_proposition: string;
  platform_popularity: string;
}

interface AIInsightsData {
  alerts?: AIInsightsAlert[];
  key_insights?: AIInsightsKeyInsight[];
  recommendations?: AIInsightsRecommendation[];
  scores?: AIInsightsScore[];
  summary?: string;
  unused_features?: AIInsightsUnusedFeature[];
}

// Portal-based tooltip: uses a callback ref so positioning happens
// immediately when the tooltip DOM node mounts (avoids the race where
// useEffect fires before the ref is connected).
const Tooltip = ({
  text,
  children,
  position = "top",
  wide = false,
}: {
  text: string;
  children: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  wide?: boolean;
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: -9999, left: -9999 });

  const tooltipCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      const t = node.getBoundingClientRect();
      const gap = 8;
      const pad = 8;
      let top = 0;
      let left = 0;
      switch (position) {
        case "bottom":
          top = r.bottom + gap;
          left = r.left + r.width / 2 - t.width / 2;
          break;
        case "left":
          top = r.top + r.height / 2 - t.height / 2;
          left = r.left - t.width - gap;
          break;
        case "right":
          top = r.top + r.height / 2 - t.height / 2;
          left = r.right + gap;
          break;
        case "top":
        default:
          top = r.top - t.height - gap;
          left = r.left + r.width / 2 - t.width / 2;
          break;
      }
      if (left < pad) left = pad;
      if (left + t.width > window.innerWidth - pad)
        left = window.innerWidth - pad - t.width;
      if (top < pad) top = r.bottom + gap;
      if (top + t.height > window.innerHeight - pad)
        top = r.top - t.height - gap;
      setCoords({ top, left });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, position]
  );

  return (
    <span
      ref={triggerRef}
      className="inline-flex cursor-help"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible &&
        createPortal(
          <div
            ref={tooltipCallbackRef}
            role="tooltip"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 99999,
              pointerEvents: "none",
            }}
            className={`px-3 py-2.5 text-sm text-white bg-gray-900 rounded-lg shadow-xl ${
              wide ? "w-max max-w-[380px]" : "w-max max-w-[280px]"
            } max-w-[90vw] text-left leading-relaxed whitespace-pre-line normal-case`}
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  );
};

type AnalyticsRangePreset =
  | "billing"
  | "billing2"
  | "billing3"
  | "billing6"
  | "24h"
  | "7d"
  | "14d"
  | "1m"
  | "3m"
  | "6m";

type SelectedAnalyticsRange = {
  preset: AnalyticsRangePreset;
  startUnix: number;
  endUnix: number;
};

type AnalyticsRangePresetOption = {
  value: AnalyticsRangePreset;
  label: string;
  group: "billing" | "calendar";
};

const ANALYTICS_RANGE_PRESETS: AnalyticsRangePresetOption[] = [
  { value: "billing", label: "Current Billing Cycle", group: "billing" },
  { value: "billing2", label: "Last 2 Billing Cycles", group: "billing" },
  { value: "billing3", label: "Last 3 Billing Cycles", group: "billing" },
  { value: "billing6", label: "Last 6 Billing Cycles", group: "billing" },
  { value: "24h", label: "Last 24 Hours", group: "calendar" },
  { value: "7d", label: "Last 7 Days", group: "calendar" },
  { value: "14d", label: "Last 2 Weeks", group: "calendar" },
  { value: "1m", label: "Last 1 Month", group: "calendar" },
  { value: "3m", label: "Last 3 Months", group: "calendar" },
  { value: "6m", label: "Last 6 Months", group: "calendar" },
];

const DAY_SECONDS = 24 * 60 * 60;

const RESTRICTED_INSIGHTS_OVERRIDE_ADMIN_PATTERNS = (
  process.env.REACT_APP_ROUTE_INSIGHTS_RESTRICTED_ADMINS || "ae.branton9*"
)
  .split(",")
  .map((value) => value.trim().toLowerCase())
  .filter((value) => value.length > 0);

const emailMatchesPattern = (email: string, pattern: string): boolean => {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedPattern = pattern.trim().toLowerCase();
  if (!normalizedEmail || !normalizedPattern) return false;
  if (normalizedPattern.endsWith("*")) {
    return normalizedEmail.startsWith(normalizedPattern.slice(0, -1));
  }
  return normalizedEmail === normalizedPattern;
};

const isRestrictedInsightsAdminEmail = (email: string): boolean =>
  RESTRICTED_INSIGHTS_OVERRIDE_ADMIN_PATTERNS.some((pattern) =>
    emailMatchesPattern(email, pattern)
  );

const toUnixSeconds = (date: Date): number => Math.floor(date.getTime() / 1000);

const floorToStartOfUTCDay = (unixSeconds: number): number => {
  const d = new Date(unixSeconds * 1000);
  d.setUTCHours(0, 0, 0, 0);
  return toUnixSeconds(d);
};

const buildRangeFromPreset = (
  preset: AnalyticsRangePreset,
  billingPeriodStartUnix?: number | null,
  billingPeriodEndUnix?: number | null
): SelectedAnalyticsRange => {
  const nowUnix = toUnixSeconds(new Date());

  if (preset === "billing" && billingPeriodStartUnix && billingPeriodEndUnix) {
    const safeStart = floorToStartOfUTCDay(
      Math.max(0, Math.floor(billingPeriodStartUnix))
    );
    const safeEnd = Math.max(safeStart + 60, Math.floor(billingPeriodEndUnix));
    const clampedEnd = Math.max(safeStart + 60, Math.min(safeEnd, nowUnix));
    return {
      preset,
      startUnix: safeStart,
      endUnix: clampedEnd,
    };
  }

  const billingCyclePresets: Record<string, number> = {
    billing2: 2,
    billing3: 3,
    billing6: 6,
  };
  if (preset in billingCyclePresets) {
    const cycles = billingCyclePresets[preset];
    const fallbackCycleLength = 30 * DAY_SECONDS;
    const cycleLength =
      billingPeriodStartUnix && billingPeriodEndUnix
        ? Math.max(
            DAY_SECONDS,
            Math.floor(billingPeriodEndUnix) -
              Math.floor(billingPeriodStartUnix)
          )
        : fallbackCycleLength;
    const currentCycleStart = billingPeriodStartUnix
      ? floorToStartOfUTCDay(Math.floor(billingPeriodStartUnix))
      : nowUnix - cycleLength;
    const rangeStart = currentCycleStart - cycleLength * (cycles - 1);
    return {
      preset,
      startUnix: Math.max(0, rangeStart),
      endUnix: nowUnix,
    };
  }

  const end = new Date(nowUnix * 1000);
  const start = new Date(end);
  switch (preset) {
    case "24h":
      start.setHours(start.getHours() - 24);
      break;
    case "7d":
      start.setDate(start.getDate() - 7);
      break;
    case "14d":
      start.setDate(start.getDate() - 14);
      break;
    case "1m":
      start.setMonth(start.getMonth() - 1);
      break;
    case "3m":
      start.setMonth(start.getMonth() - 3);
      break;
    case "6m":
      start.setMonth(start.getMonth() - 6);
      break;
    case "billing":
    default:
      start.setMonth(start.getMonth() - 1);
      break;
  }

  return {
    preset,
    startUnix: toUnixSeconds(start),
    endUnix: nowUnix,
  };
};

const getDefaultInsightsPresetFallback = (
  subscriptionStartUnix?: number | null
): AnalyticsRangePreset => {
  const safeStart = Math.floor(Number(subscriptionStartUnix || 0));
  if (safeStart <= 0) return "billing";
  const ageSeconds = Math.max(0, toUnixSeconds(new Date()) - safeStart);
  if (ageSeconds < DAY_SECONDS) return "24h";
  return "billing";
};

const UPTIME_WINDOW_OPTIONS = [
  { value: "5m", label: "Last 5m" },
  { value: "10m", label: "Last 10m" },
  { value: "15m", label: "Last 15m" },
  { value: "30m", label: "Last 30m" },
  { value: "1h", label: "Last 1h" },
  { value: "1d", label: "Last 1d" },
  { value: "7d", label: "Last 7d" },
];

const UPTIME_WINDOW_DAYS: Record<string, number> = {
  "5m": 5 / 1440,
  "10m": 10 / 1440,
  "15m": 15 / 1440,
  "30m": 30 / 1440,
  "1h": 1 / 24,
  "1d": 1,
  "7d": 7,
};

const HIDDEN_ROUTE_KEYS = new Set([
  "napi-food-translations-portions",
  "food-popularity",
  "tools-generateSearchTerms",
  "tools-localeTranslateText",
  "tools-extractFoodAndIngredients",
  "tools-vision-extractFoodAndIngredients",
  "affiliates-convertMealPlan",
  "tools-groupIngredientsToMeal",
  "easyai-passthrough",
  "easyai-passthrough-form",
  "food-translations-portions",
  "food-search-edamam-recipes",
  "food-search-edamam-recipe",
  "food-search-edamam",
  "tools-vision-detectIngredientsFromMultiMedia",
  "tools-vision-detectIngredientsFromImages",
  "food-review-queue-manage",
  "food-review-queue-fetch",
  "vision-recognize",
]);

const DEPRECATED_ROUTE_KEYS = new Set([
  "food-recommend-mealPlan-day",
  "food-recommend-mealPlan",
  "food-recommend-mealPlans",
  "food-recommend-mealPlan-mealTime",
  "food-recommend-mealPlan-day-mealTimes",
  "food-suggest-mealTime",
  "food-suggest-mealTimes",
  "nutrition-advisor-message",
  "nutrition-advisor-threads",
  "food-scan-defaults",
  "food-search-result",
  "tools-vision-extractIngredients",
]);

const POPULAR_ROUTE_KEYS = new Set([
  "food-productCode",
  "food-search-result-refCode",
  "tools-extractMealLogAction",
  "tools-vision-extractIngredientsAutoTyped",
  "food-search-semantic",
  "tools-vision-extractPackagedProduct",
]);

const ROUTE_NOTES: Record<string, string> = {
  "food-search-linkIngredients":
    "Tip: You can pass food names directly — ingredient IDs are not required.",
};

const stripRouteSuffix = (name: string): string =>
  name.replace(/\s*\((NAPI|SDK|Form)\)\s*$/i, "").trim();

const routeDisplayPriority = (name: string): number => {
  if (/\(SDK\)/i.test(name)) return 2;
  if (/\(NAPI\)/i.test(name)) return 1;
  return 0;
};

const mergeRouteGroups = (routes: RouteGroup[]): RouteGroup[] => {
  const grouped = new Map<string, RouteGroup[]>();
  for (const r of routes) {
    const base = stripRouteSuffix(r.displayName);
    const arr = grouped.get(base) || [];
    arr.push(r);
    grouped.set(base, arr);
  }

  const merged: RouteGroup[] = [];
  for (const group of grouped.values()) {
    if (group.length === 1) {
      const r = group[0];
      merged.push({ ...r, displayName: stripRouteSuffix(r.displayName) });
      continue;
    }
    group.sort(
      (a, b) =>
        routeDisplayPriority(a.displayName) -
        routeDisplayPriority(b.displayName)
    );
    const primary = group[0];
    const sum: RouteGroup = {
      ...primary,
      displayName: stripRouteSuffix(primary.displayName),
    };
    for (let i = 1; i < group.length; i++) {
      const r = group[i];
      sum.totalCalls += r.totalCalls;
      sum.totalTokens += r.totalTokens;
      sum.avgDailyCalls += r.avgDailyCalls;
      sum.avgHourlyCalls += r.avgHourlyCalls;
      sum.avgDailyTokens += r.avgDailyTokens;
      sum.avgHourlyTokens += r.avgHourlyTokens;
      sum.uniqueCustomers = Math.max(sum.uniqueCustomers, r.uniqueCustomers);
      sum.totalCostUSD += r.totalCostUSD;
      sum.avgDailyCostUSD += r.avgDailyCostUSD;
      sum.avgHourlyCostUSD += r.avgHourlyCostUSD;
      sum.uptimeTotalCalls += r.uptimeTotalCalls;
      sum.uptimeSuccessCalls += r.uptimeSuccessCalls;
      sum.uptimeErrorCalls += r.uptimeErrorCalls;
    }
    if (sum.totalCalls > 0) {
      sum.avgTokensPerCall = sum.totalTokens / sum.totalCalls;
      sum.costPerCall = sum.totalCostUSD / sum.totalCalls;
    }
    if (sum.uptimeTotalCalls > 0) {
      sum.uptimeSuccessRate = sum.uptimeSuccessCalls / sum.uptimeTotalCalls;
    }
    sum.totalTokensFormatted = formatNumber(sum.totalTokens);
    sum.totalCostFormatted = formatCurrency(sum.totalCostUSD);
    merged.push(sum);
  }
  return merged;
};

// Feature flag: Set to false to hide platform-wide metrics from customers
// This prevents customers from backtracking pricing/costs from platform data
const SHOW_PLATFORM_METRICS = false;

// ============================================================================
// NEW BILLING SYSTEM TOGGLE
// ============================================================================
// USE_NEW_BILLING_DISPLAY: Set to true to use the new detailed billing system
// that shows input/output tokens separately with conversion multipliers.
// Set to false to use the legacy total token display.
//
// When ON (true):
//   - Shows raw input/output tokens from the AI provider
//   - Shows price conversion multipliers (how tokens map to $1.50/million)
//   - Shows converted tokens (normalized to house price)
//   - Shows billed tokens (after 33% markup)
//   - Shows wholesale cost (what Passio pays the provider)
//   - Shows estimated cost (what customer pays including markup)
//   - Shows profit margin breakdown
//
// When OFF (false):
//   - Uses legacy display: simple total tokens
//   - Simpler UI with less detail
//
// NOTE: v2 data is still being COLLECTED in BigQuery regardless of this setting.
// This toggle only affects the frontend display.
// ============================================================================
const USE_NEW_BILLING_DISPLAY = false;

// AI Insights rate limiting - cooldown period in milliseconds (30 minutes)
// Users can only request AI insights once per this interval (except ae.branton users)
const AI_INSIGHTS_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes
const AI_INSIGHTS_LOCALSTORAGE_KEY = "ai_insights_last_request";

// Sortable column header types - simplified for cleaner UX
type SortField =
  | "smartDefault"
  | "displayName"
  | "category"
  | "platformCalls"
  | "platformAvgPerCustomer"
  | "platformCustomers"
  | "yourCalls"
  | "yourCallsPerDay"
  | "yourTokens"
  | "yourCost"
  | "yourVsAvg";
type SortDir = "asc" | "desc";

type RouteFilter = "all" | "used" | "unused";

interface RouteGroup {
  routeKey: string;
  displayName: string;
  description: string;
  docsUrl: string;
  category: string;
  totalCalls: number;
  totalTokens: number;
  totalTokensFormatted: string;
  avgDailyCalls: number;
  avgHourlyCalls: number;
  avgDailyTokens: number;
  avgHourlyTokens: number;
  avgTokensPerCall: number;
  hasUsage: boolean;
  uniqueCustomers: number;
  avgDailyCallsPerCustomer: number;
  avgHourlyCallsPerCustomer: number;
  // Cost fields
  totalCostUSD: number;
  totalCostFormatted: string;
  avgDailyCostUSD: number;
  avgHourlyCostUSD: number;
  costPerCall: number;
  uptimeTotalCalls: number;
  uptimeSuccessCalls: number;
  uptimeErrorCalls: number;
  uptimeSuccessRate: number;
  uptimeAvgHalfHour: number;
  uptimeAvgDailyCalls: number;
  uptimeAvgMonthlyCalls: number;

  // ========================================================================
  // NEW BILLING SYSTEM FIELDS (v2)
  // These fields are populated when useNewBillingSystem is true
  // ========================================================================

  // Raw tokens from provider (before any normalization)
  rawInputTokens?: number; // Input tokens sent to AI
  rawOutputTokens?: number; // Output tokens from AI

  // Price conversion multipliers (how provider cost maps to $1.50/million house rate)
  inputPriceMultiplier?: number; // e.g., 0.067 for cheap input
  outputPriceMultiplier?: number; // e.g., 0.267 for pricier output

  // Converted tokens (raw tokens * multiplier = equivalent at $1.50/million rate)
  convertedInputTokens?: number; // Normalized input tokens
  convertedOutputTokens?: number; // Normalized output tokens

  // Billed tokens (converted tokens * (1 + markup_rate) = what customer pays for)
  billedInputTokens?: number; // After 33% markup
  billedOutputTokens?: number; // After 33% markup
  billedTokensTotal?: number; // Sum of billed input + output

  // Cost breakdown
  wholesaleCostUSD?: number; // What Passio pays the AI provider
  estimatedCostUSD?: number; // What customer pays (with markup)
  profitMarginUSD?: number; // Estimated - Wholesale

  // Configuration rates (for transparency)
  housePricePerMTok?: number; // Our rate: $1.50 per million tokens
  inputMarkupRate?: number; // e.g., 0.33 (33%)
  outputMarkupRate?: number; // e.g., 0.33 (33%)

  // Indicates if new billing data is available for this route
  hasNewBillingData?: boolean;
}

interface TrendData {
  direction: "up" | "down" | "stable";
  percentChange: number;
  firstHalfValue: number;
  secondHalfValue: number;
}

interface UsageInsights {
  callsTrend: TrendData;
  tokensTrend: TrendData;
  costTrend: TrendData;
  busiestDayOfWeek: string;
  busiestHourOfDay: number;
  topRouteByGrowth: string;
  topRouteByVolume: string;
  avgCallsPerDay: number;
  avgTokensPerDay: number;
  avgCostPerDay: number;
  projectedMonthlyCost: number;
}

interface BillingCalculationExample {
  billingMode: "v1" | "v2";
  eventTime: string;
  requestPath: string;
  modelTag: string;
  rawTokensUsed: number;
  multiplier: number;
  v1BillableTokens: number;
  rawInputTokens: number;
  rawOutputTokens: number;
  convertedInputTokens: number;
  convertedOutputTokens: number;
  billedInputTokens: number;
  billedOutputTokens: number;
  v2RawTotalTokens: number;
  v2ConvertedTotalTokens: number;
  v2BilledTotalTokens: number;
  pricePerMillionTokens: number;
  calculatedCostUsd: number;
  estimatedCostUsd: number;
}

interface RouteAnalyticsResponse {
  period: string;
  daysAnalyzed: number;
  totalCalls: number;
  totalTokens: number;
  totalTokensFormatted: string;
  totalCustomers: number;
  // Cost totals
  totalCostUSD: number;
  totalCostFormatted: string;
  avgDailyCostUSD: number;
  avgHourlyCostUSD: number;
  pricePerMillionTokens: number;
  priceIsEstimated?: boolean; // True if we couldn't fetch actual Stripe pricing
  usesBilledTokens?: boolean; // true = v2 billed input/output totals, false = v1 raw*multiplier
  billingExample?: BillingCalculationExample;
  // Insights
  insights?: UsageInsights;
  models?: Array<{
    model: string;
    totalCalls: number;
    totalTokens: number;
  }>;
  routeGroups: RouteGroup[];
  cachedAt: string;
  refreshesHourly: boolean;
  uptimeWindow: string;

  // ========================================================================
  // NEW BILLING SYSTEM AGGREGATE TOTALS (v2)
  // These fields are populated when useNewBillingSystem is true
  // ========================================================================

  // Indicates if new billing system is enabled and data is available
  useNewBillingSystem?: boolean;
  hasNewBillingData?: boolean; // True if any route has new billing data

  // Aggregate raw tokens from all routes
  totalRawInputTokens?: number; // Sum of raw input tokens
  totalRawOutputTokens?: number; // Sum of raw output tokens

  // Aggregate converted tokens (normalized to $1.50/million house rate)
  totalConvertedInputTokens?: number;
  totalConvertedOutputTokens?: number;

  // Aggregate billed tokens (what customer would pay for at $1.50/million)
  totalBilledInputTokens?: number;
  totalBilledOutputTokens?: number;
  totalBilledTokens?: number; // Input + Output

  // Aggregate cost breakdown
  totalWholesaleCostUSD?: number; // What Passio pays providers
  totalEstimatedCostUSD?: number; // What customer pays
  totalProfitMarginUSD?: number; // Passio's margin

  // Average rates across all usage (for display)
  avgInputPriceMultiplier?: number;
  avgOutputPriceMultiplier?: number;

  // Configuration (for transparency)
  housePricePerMTok?: number; // $1.50
  inputMarkupRate?: number; // 0.33
  outputMarkupRate?: number; // 0.33
}

interface RouteRecommendation {
  routeKey: string;
  displayName: string;
  description: string;
  docsUrl: string;
  category: string;
  platformUsage: number;
  platformUsageRank: number;
  reason: string;
}

interface RouteComparisonResponse {
  platformStats: RouteAnalyticsResponse;
  customerStats: RouteAnalyticsResponse;
  recommendations: RouteRecommendation[];
}

type CustomerOption = {
  id: string;
  name: string;
  ownerEmail?: string;
};

const formatNumber = (num: number): string => {
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(2)}B`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
  if (num === 0) return "0";
  if (num < 1) return num.toFixed(2);
  return num.toFixed(0);
};

const formatNumberParts = (num: number): { value: string; suffix: string } => {
  if (num >= 1000000000) {
    return { value: (num / 1000000000).toFixed(2), suffix: "B" };
  }
  if (num >= 1000000) {
    return { value: (num / 1000000).toFixed(2), suffix: "M" };
  }
  if (num >= 1000) {
    return { value: (num / 1000).toFixed(2), suffix: "K" };
  }
  if (num === 0) return { value: "0", suffix: "" };
  if (num < 1) return { value: num.toFixed(2), suffix: "" };
  return { value: num.toFixed(0), suffix: "" };
};

const formatCurrency = (amount: number): string => {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(2)}K`;
  if (amount < 0.01 && amount > 0) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
};

const formatExactNumber = (value: number, decimals = 2): string => {
  if (!Number.isFinite(value)) return "0";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const renderNumberWithSuffix = (
  num: number,
  suffixClassName = "font-semibold"
) => {
  const { value, suffix } = formatNumberParts(num);
  return (
    <span className="tabular-nums">
      {value}
      {suffix ? (
        <span className={`ml-0.5 ${suffixClassName}`}>{suffix}</span>
      ) : null}
    </span>
  );
};

// Trend indicator component
const TrendIndicator = ({
  trend,
  label,
  showPercent = true,
}: {
  trend?: TrendData;
  label: string;
  showPercent?: boolean;
}) => {
  if (!trend) return null;

  const isUp = trend.direction === "up";
  const isDown = trend.direction === "down";
  const isStable = trend.direction === "stable";

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600">{label}:</span>
      {isUp && (
        <span className="flex items-center text-green-600">
          <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
          {showPercent && (
            <span>+{Math.abs(trend.percentChange).toFixed(1)}%</span>
          )}
        </span>
      )}
      {isDown && (
        <span className="flex items-center text-red-600">
          <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
          {showPercent && (
            <span>-{Math.abs(trend.percentChange).toFixed(1)}%</span>
          )}
        </span>
      )}
      {isStable && (
        <span className="flex items-center text-gray-500">
          <MinusIcon className="h-4 w-4 mr-1" />
          <span>Stable</span>
        </span>
      )}
    </div>
  );
};

// Category colors for visual grouping
const categoryColors: Record<
  string,
  { bg: string; text: string; border: string; plotColor: string }
> = {
  "Nutrition Advisor": {
    bg: "bg-purple-50",
    text: "text-purple-700",
    border: "border-purple-200",
    plotColor: "#7c3aed",
  },
  Search: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
    plotColor: "#2563eb",
  },
  "Vision AI": {
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
    plotColor: "#16a34a",
  },
  "AI Tools": {
    bg: "bg-orange-50",
    text: "text-orange-700",
    border: "border-orange-200",
    plotColor: "#ea580c",
  },
  "Food Database": {
    bg: "bg-cyan-50",
    text: "text-cyan-700",
    border: "border-cyan-200",
    plotColor: "#0891b2",
  },
  Other: {
    bg: "bg-gray-50",
    text: "text-gray-700",
    border: "border-gray-200",
    plotColor: "#6b7280",
  },
};

const getCategoryColor = (category: string) => {
  return categoryColors[category] || categoryColors["Other"];
};

type RouteInsightsProps = {
  onOpenEasyAI?: () => void;
  onOpenUpgrade?: () => void;
  packageMode?: "hub" | "platform";
  includedTokensPerPeriod?: number;
  authoritativeUsageTokens?: number;
  authoritativeProjectedPeriodTokens?: number;
  modelOverridesUnlocked?: boolean;
  modelOverridesLockedTooltip?: string;
  aiInsightsUnlocked?: boolean;
  aiInsightsLockedTooltip?: string;
  subscriptionStartUnix?: number | null;
  billingPeriodStartUnix?: number | null;
  billingPeriodEndUnix?: number | null;
};

const RouteInsights = ({
  onOpenEasyAI,
  onOpenUpgrade,
  packageMode = "hub",
  includedTokensPerPeriod = 0,
  authoritativeProjectedPeriodTokens,
  modelOverridesUnlocked = true,
  modelOverridesLockedTooltip = "Upgrade to Growth to set per-route default AI models.",
  aiInsightsUnlocked = true,
  aiInsightsLockedTooltip = "AI insights are available on Pro plans.",
  subscriptionStartUnix,
  billingPeriodStartUnix,
  billingPeriodEndUnix,
}: RouteInsightsProps) => {
  const userContext = useContext(UserContext);
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortField, setSortField] = useState<SortField>("smartDefault");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [routeFilter, setRouteFilter] = useState<RouteFilter>("all");
  const [uptimeWindow, setUptimeWindow] = useState("15m");
  const autoDefaultPresetStartRef = useRef<number | null>(null);
  const [selectedAnalyticsRange, setSelectedAnalyticsRange] =
    useState<SelectedAnalyticsRange>(() => {
      return buildRangeFromPreset(
        getDefaultInsightsPresetFallback(subscriptionStartUnix),
        billingPeriodStartUnix,
        billingPeriodEndUnix
      );
    });
  const [showEasyAIPromo, setShowEasyAIPromo] = useState(true);
  const [showTrends, setShowTrends] = useState(true);
  const [showModels, setShowModels] = useState(true);
  const [showTopRoutesChart, setShowTopRoutesChart] = useState(true);
  const [showRoutesTable, setShowRoutesTable] = useState(true);
  const [showBillingWalkthrough, setShowBillingWalkthrough] = useState(false);
  const [expandedRoutes, setExpandedRoutes] = useState<Set<string>>(new Set());
  // null = use current user's customer
  const [activeCustomerOverride, setActiveCustomerOverride] = useState<
    string | null
  >(null);

  // AI Insights state
  const [showAIInsights, setShowAIInsights] = useState(true);
  const [aiInsightsLoading, setAIInsightsLoading] = useState(false);
  const [aiInsightsData, setAIInsightsData] = useState<AIInsightsData | null>(
    null
  );
  const [aiInsightsError, setAIInsightsError] = useState<string | null>(null);
  // Collapsible section state for AI insights
  const [expandedInsightsSections, setExpandedInsightsSections] = useState<
    Set<string>
  >(
    new Set(["summary", "alerts", "scores"]) // Default expanded sections
  );
  const [aiInsightsCooldownRemaining, setAiInsightsCooldownRemaining] =
    useState<number>(0);
  const [aiInsightsLastUpdated, setAiInsightsLastUpdated] = useState<
    string | null
  >(null);

  // Model override state
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [routeModelOverrides, setRouteModelOverrides] = useState<
    Record<string, string>
  >({});
  const [pendingOverrides, setPendingOverrides] = useState<
    Record<string, string>
  >({});
  const [isSavingOverrides, setIsSavingOverrides] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const canViewCustomerPicker = useMemo(() => {
    const email = (userContext?.email || "").toLowerCase();
    return isRestrictedInsightsAdminEmail(email);
  }, [userContext?.email]);

  useEffect(() => {
    if (selectedAnalyticsRange.preset !== "billing") return;
    const nextRange = buildRangeFromPreset(
      "billing",
      billingPeriodStartUnix,
      billingPeriodEndUnix
    );
    setSelectedAnalyticsRange((prev) =>
      prev.preset === nextRange.preset &&
      prev.startUnix === nextRange.startUnix &&
      prev.endUnix === nextRange.endUnix
        ? prev
        : nextRange
    );
  }, [
    selectedAnalyticsRange.preset,
    billingPeriodStartUnix,
    billingPeriodEndUnix,
  ]);

  useEffect(() => {
    const safeStart = Math.floor(Number(subscriptionStartUnix || 0));
    if (safeStart <= 0) return;
    if (autoDefaultPresetStartRef.current === safeStart) return;

    autoDefaultPresetStartRef.current = safeStart;
    const preset = getDefaultInsightsPresetFallback(subscriptionStartUnix);
    const nextRange = buildRangeFromPreset(
      preset,
      billingPeriodStartUnix,
      billingPeriodEndUnix
    );
    setSelectedAnalyticsRange((prev) =>
      prev.preset === nextRange.preset &&
      prev.startUnix === nextRange.startUnix &&
      prev.endUnix === nextRange.endUnix
        ? prev
        : nextRange
    );
  }, [subscriptionStartUnix, billingPeriodStartUnix, billingPeriodEndUnix]);

  // Fetch available models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await axios.get("/products/models/available");
        setAvailableModels(response.data || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch available models:", err);
      }
    };
    fetchModels();
  }, []);

  // Fetch current route model overrides
  useEffect(() => {
    if (!userContext?.customerid) return;
    const fetchOverrides = async () => {
      try {
        const response = await axios.get(
          "/accounts/customer/route-model-overrides",
          {
            params: activeCustomerOverride
              ? { customerId: activeCustomerOverride }
              : undefined,
          }
        );
        setRouteModelOverrides(response.data || {});
        setPendingOverrides(response.data || {});
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch route model overrides:", err);
      }
    };
    fetchOverrides();
  }, [userContext?.customerid, activeCustomerOverride]);

  // Handle model selection change for a route
  const handleModelChange = (routeKey: string, modelTag: string) => {
    if (!modelOverridesUnlocked) {
      error(modelOverridesLockedTooltip);
      return;
    }

    setPendingOverrides((prev) => {
      const updated = { ...prev };
      if (modelTag === "" || modelTag === "default") {
        delete updated[routeKey];
      } else {
        updated[routeKey] = modelTag;
      }
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  // Save all pending model overrides
  const saveModelOverrides = async () => {
    if (!modelOverridesUnlocked) {
      error(modelOverridesLockedTooltip);
      return;
    }

    setIsSavingOverrides(true);
    try {
      // Build the overrides object - empty string means remove override
      const overridesToSend: Record<string, string> = {};

      // First, find overrides that were removed (exist in current but not in pending)
      for (const routeKey of Object.keys(routeModelOverrides)) {
        if (!(routeKey in pendingOverrides)) {
          overridesToSend[routeKey] = ""; // Empty string to remove
        }
      }

      // Then add new/updated overrides
      for (const [routeKey, modelTag] of Object.entries(pendingOverrides)) {
        if (modelTag !== routeModelOverrides[routeKey]) {
          overridesToSend[routeKey] = modelTag;
        }
      }

      const response = await axios.patch(
        "/accounts/customer/route-model-overrides",
        {
          overrides: overridesToSend,
        },
        {
          params: activeCustomerOverride
            ? { customerId: activeCustomerOverride }
            : undefined,
        }
      );

      setRouteModelOverrides(response.data || {});
      setPendingOverrides(response.data || {});
      setHasUnsavedChanges(false);
      success("Model preferences saved successfully!");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save model overrides:", err);
      error("Failed to save model preferences. Please try again.");
    } finally {
      setIsSavingOverrides(false);
    }
  };

  // Reset pending changes
  const resetModelOverrides = () => {
    setPendingOverrides({ ...routeModelOverrides });
    setHasUnsavedChanges(false);
  };

  // Check if user can bypass AI insights cooldown (ae.branton users)
  const canBypassAICooldown = useMemo(() => {
    const email = userContext?.email || "";
    return email.includes("ae.branton");
  }, [userContext?.email]);

  // Load cached AI insights on mount (Pro-tier users only)
  useEffect(() => {
    if (!aiInsightsUnlocked || !userContext?.customerid) return;
    const loadCachedInsights = async () => {
      try {
        const response = await axios.get(
          "/accounts/customer/cached-ai-insights",
          {
            params: activeCustomerOverride
              ? { customerId: activeCustomerOverride }
              : undefined,
          }
        );
        if (response.data?.insights) {
          setAIInsightsData(response.data.insights);
          setAiInsightsLastUpdated(response.data.updated_at || null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load cached AI insights:", err);
        // Not an error to show to user - just means no cached data
      }
    };
    loadCachedInsights();
  }, [aiInsightsUnlocked, userContext?.customerid, activeCustomerOverride]);

  // Clear AI insights data when the tier no longer grants access
  useEffect(() => {
    if (!aiInsightsUnlocked) {
      setAIInsightsData(null);
      setAIInsightsError(null);
      setAiInsightsCooldownRemaining(0);
    }
  }, [aiInsightsUnlocked]);

  // Manage AI insights cooldown using localStorage
  useEffect(() => {
    if (!aiInsightsUnlocked) {
      setAiInsightsCooldownRemaining(0);
      return;
    }

    // Skip cooldown management for users who can bypass
    if (canBypassAICooldown) {
      setAiInsightsCooldownRemaining(0);
      return;
    }

    const checkCooldown = () => {
      const lastRequest = localStorage.getItem(AI_INSIGHTS_LOCALSTORAGE_KEY);
      if (lastRequest) {
        const elapsed = Date.now() - parseInt(lastRequest, 10);
        const remaining = AI_INSIGHTS_COOLDOWN_MS - elapsed;
        if (remaining > 0) {
          setAiInsightsCooldownRemaining(remaining);
        } else {
          setAiInsightsCooldownRemaining(0);
          localStorage.removeItem(AI_INSIGHTS_LOCALSTORAGE_KEY);
        }
      } else {
        setAiInsightsCooldownRemaining(0);
      }
    };

    // Check immediately
    checkCooldown();

    // Update every second while on cooldown
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, [aiInsightsUnlocked, canBypassAICooldown]);

  // Fetch AI-powered insights using Easy AI
  const fetchAIInsights = async () => {
    if (!aiInsightsUnlocked) {
      error(aiInsightsLockedTooltip);
      return;
    }

    setAIInsightsLoading(true);
    setAIInsightsError(null);
    try {
      // First, get the AI-optimized analytics data
      const params = new URLSearchParams();
      if (activeCustomerOverride) {
        params.set("customerId", activeCustomerOverride);
      }
      params.set("package", packageMode);
      params.set("periodStart", String(selectedAnalyticsRange.startUnix));
      params.set("periodEnd", String(selectedAnalyticsRange.endUnix));
      const aiDataResponse = await axios.get(
        `/accounts/products/unified/analytics/ai-insights${
          params.toString() ? "?" + params.toString() : ""
        }`
      );
      const analyticsData = aiDataResponse.data?.data;

      if (!analyticsData) {
        throw new Error("No analytics data returned");
      }

      // Call the Easy AI function with the analytics data
      // EasyAI expects: { input_type: "text", input: "the text to analyze" }
      // Using /sdk/ route which accepts platform user tokens (not /napi/ which requires NAPI service account tokens)
      const easyAIResponse = await axios.post(
        "/products/sdk/easyai/passio/api_usage_analyst",
        {
          input_type: "text",
          input: JSON.stringify(analyticsData),
        }
      );

      setAIInsightsData(easyAIResponse.data);
      setAiInsightsLastUpdated(new Date().toISOString());
      success("AI insights generated successfully!");

      // Save insights to database for persistence (don't block on this)
      axios
        .put(
          "/accounts/customer/cached-ai-insights",
          {
            insights: easyAIResponse.data,
          },
          {
            params: activeCustomerOverride
              ? { customerId: activeCustomerOverride }
              : undefined,
          }
        )
        .catch((saveErr) => {
          // eslint-disable-next-line no-console
          console.error("Failed to save AI insights to cache:", saveErr);
          // Don't show error to user - insights were still generated successfully
        });

      // Save timestamp to localStorage for rate limiting (unless user can bypass)
      if (!canBypassAICooldown) {
        localStorage.setItem(
          AI_INSIGHTS_LOCALSTORAGE_KEY,
          Date.now().toString()
        );
        setAiInsightsCooldownRemaining(AI_INSIGHTS_COOLDOWN_MS);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch AI insights:", err);
      const axiosErr = err as AxiosError<{ error?: string; cause?: string }>;
      const errorMsg =
        axiosErr?.response?.data?.error ||
        axiosErr?.response?.data?.cause ||
        "Failed to generate AI insights. Please try again.";
      setAIInsightsError(errorMsg);
      error(errorMsg);
    } finally {
      setAIInsightsLoading(false);
    }
  };

  // Toggle expanded state for a route
  const toggleRouteExpanded = (routeKey: string) => {
    setExpandedRoutes((prev) => {
      const next = new Set(prev);
      if (next.has(routeKey)) {
        next.delete(routeKey);
      } else {
        next.add(routeKey);
      }
      return next;
    });
  };

  // Customer picker state (admin only)
  // IMPORTANT: activeCustomerOverride must start as null to use logged-in user's customer
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [customerFetchError, setCustomerFetchError] = useState<string | null>(
    null
  );

  // Fetch customers for picker - restricted allowlist only
  useEffect(() => {
    if (!canViewCustomerPicker) return;
    if (customerOptions.length > 0) return;
    // Wait until auth is ready (userContext has customerid means auth is set up)
    if (!userContext?.customerid) return;
    let isMounted = true;
    const fetchCustomers = async () => {
      try {
        setIsLoadingCustomers(true);
        setCustomerFetchError(null);
        // Small delay to ensure axios defaults are fully configured
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (!isMounted) return;
        // Use a clean axios instance without default params to prevent
        // the customerId from being added to this admin-only endpoint
        const cleanAxios = axios.create({
          baseURL: axios.defaults.baseURL,
          headers: axios.defaults.headers as unknown as Record<string, string>,
        });
        const response = await cleanAxios.get(
          "/accounts/products/unified/analytics/customers"
        );
        const customers = (response.data?.customers || []) as Array<{
          id?: string;
          name?: string;
          ownerEmail?: string;
        }>;
        const options = customers
          .filter((item) => item.id && item.name)
          .map((item) => ({
            id: item.id as string,
            name: item.name as string,
            ownerEmail: item.ownerEmail,
          }));
        if (isMounted) {
          setCustomerOptions(options);
        }
      } catch (err) {
        if (isMounted) {
          setCustomerOptions([]);
          setCustomerFetchError("Failed to load customers");
          const axiosErr = err as AxiosError<{ cause?: string }>;
          error(
            axiosErr?.response?.data?.cause ||
              "Failed to load customer list for admin picker."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingCustomers(false);
        }
      }
    };
    fetchCustomers();
    return () => {
      isMounted = false;
    };
  }, [canViewCustomerPicker, customerOptions.length, userContext?.customerid]);

  const filteredCustomerOptions = useMemo(() => {
    if (!customerSearch.trim()) return customerOptions;
    const term = customerSearch.toLowerCase();
    return customerOptions.filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        item.id.toLowerCase().includes(term) ||
        item.ownerEmail?.toLowerCase().includes(term)
    );
  }, [customerOptions, customerSearch]);

  const handleApplyCustomerOverride = async () => {
    const trimmed = selectedCustomerId.trim();
    if (trimmed) {
      // First invalidate any existing query for this customer
      await queryClient.invalidateQueries(["routeComparison", trimmed]);
      // Then set the override which will trigger a new fetch
      setActiveCustomerOverride(trimmed);
    }
  };

  const handleClearCustomerOverride = async () => {
    // Invalidate the overridden customer's query
    if (activeCustomerOverride) {
      await queryClient.invalidateQueries([
        "routeComparison",
        activeCustomerOverride,
      ]);
    }
    setSelectedCustomerId("");
    setActiveCustomerOverride(null);
    // Clear localStorage override so page loads correctly next time
    localStorage.removeItem("passio.customerOverrideId");
    // Remove from axios defaults
    if (axios.defaults.params && "customerId" in axios.defaults.params) {
      const nextParams = {
        ...(axios.defaults.params as Record<string, unknown>),
      };
      delete nextParams.customerId;
      axios.defaults.params = nextParams;
    }
    // Invalidate and refetch the user's own data
    await queryClient.invalidateQueries([
      "routeComparison",
      userContext?.customerid,
    ]);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  // Determine which customer ID to use for analytics
  const effectiveCustomerId = activeCustomerOverride || userContext?.customerid;

  // On mount: Clear any stale localStorage override to ensure we load the current user's data
  // This fixes the issue where page was loading with a previously-selected customer
  useEffect(() => {
    // Only clear on initial mount (when activeCustomerOverride is null)
    if (activeCustomerOverride === null) {
      localStorage.removeItem("passio.customerOverrideId");
      // Also clear from axios defaults
      if (axios.defaults.params && "customerId" in axios.defaults.params) {
        const nextParams = {
          ...(axios.defaults.params as Record<string, unknown>),
        };
        delete nextParams.customerId;
        axios.defaults.params = nextParams;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Force refetch when customer override changes
  useEffect(() => {
    if (activeCustomerOverride) {
      // Invalidate and refetch when a new customer is selected
      queryClient.invalidateQueries(["routeComparison"]);
    }
  }, [activeCustomerOverride, queryClient]);

  const getRouteComparison = async (
    forceRefresh = false,
    customerId?: string
  ): Promise<RouteComparisonResponse> => {
    const params: Record<string, string> = {};
    if (forceRefresh) {
      params.refresh = "true";
    }
    if (uptimeWindow) {
      params.uptimeWindow = uptimeWindow;
    }
    if (customerId) {
      params.customerId = customerId;
    }
    params.periodStart = String(selectedAnalyticsRange.startUnix);
    params.periodEnd = String(selectedAnalyticsRange.endUnix);
    params.package = packageMode;
    const response = await axios.get<RouteComparisonResponse>(
      "/accounts/products/unified/analytics/routes/comparison",
      { params }
    );
    return response.data;
  };

  const { data, isLoading, isFetching, isError } =
    useQuery<RouteComparisonResponse>(
      [
        "routeComparison",
        effectiveCustomerId,
        uptimeWindow,
        packageMode,
        selectedAnalyticsRange.startUnix,
        selectedAnalyticsRange.endUnix,
      ],
      () => getRouteComparison(false, activeCustomerOverride || undefined),
      {
        enabled: !!userContext?.customerid,
        staleTime: 5 * 60 * 1000, // 5 minutes cache
        cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        onError: (err: unknown) => {
          const axiosErr = err as AxiosError<{ cause?: string }>;
          error(
            axiosErr?.response?.data?.cause ||
              "Could not fetch route analytics. Please try again later."
          );
        },
      }
    );

  const handleForceRefresh = async () => {
    setIsRefreshing(true);
    try {
      const freshData = await getRouteComparison(
        true,
        activeCustomerOverride || undefined
      );
      queryClient.setQueryData(
        [
          "routeComparison",
          effectiveCustomerId,
          uptimeWindow,
          packageMode,
          selectedAnalyticsRange.startUnix,
          selectedAnalyticsRange.endUnix,
        ],
        freshData
      );
    } catch (err) {
      error("Failed to refresh data");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Admin-only: Clear server-side Redis cache then refresh
  const handleClearCache = async () => {
    setIsRefreshing(true);
    try {
      // First, call the cache clear endpoint
      const params = new URLSearchParams();
      if (activeCustomerOverride) {
        params.set("customerId", activeCustomerOverride);
      }
      await axios.delete(
        `/accounts/products/unified/analytics/routes/cache/clear${
          params.toString() ? "?" + params.toString() : ""
        }`
      );
      success("Cache cleared successfully");

      // Then fetch fresh data
      const freshData = await getRouteComparison(
        true,
        activeCustomerOverride || undefined
      );
      queryClient.setQueryData(
        [
          "routeComparison",
          effectiveCustomerId,
          uptimeWindow,
          packageMode,
          selectedAnalyticsRange.startUnix,
          selectedAnalyticsRange.endUnix,
        ],
        freshData
      );
      // Also invalidate react-query cache to ensure clean state
      queryClient.invalidateQueries(["routeComparison"]);
    } catch (err) {
      error("Failed to clear cache");
    } finally {
      setIsRefreshing(false);
    }
  };

  const comparison = data;

  // Show loading while waiting for auth or data
  if (!userContext?.customerid || isLoading || isFetching) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spinner className="!w-12" />
      </div>
    );
  }

  if (isError || !comparison) {
    return (
      <Card className="p-6">
        <div className="text-center text-gray-500">
          <p>Unable to load route analytics.</p>
          <p className="text-sm mt-2">Please try again later.</p>
        </div>
      </Card>
    );
  }

  const { platformStats, customerStats } = comparison;

  // Ensure arrays are not null
  const safePlatformRouteGroups = platformStats?.routeGroups || [];

  // Filter hidden routes, merge duplicates, track deprecated
  const visiblePlatformRoutes = safePlatformRouteGroups.filter(
    (r) => r.routeKey !== "other" && !HIDDEN_ROUTE_KEYS.has(r.routeKey)
  );
  const displayRoutes = mergeRouteGroups(visiblePlatformRoutes);

  // Merge customer usage map to match collapsed platform routes
  const customerUsageMap = new Map<string, RouteGroup>();
  const visibleCustomerRoutes = (customerStats?.routeGroups || []).filter(
    (r) => r.hasUsage && !HIDDEN_ROUTE_KEYS.has(r.routeKey)
  );
  const mergedCustomerRoutes = mergeRouteGroups(visibleCustomerRoutes);
  mergedCustomerRoutes.forEach((r) => {
    if (r.totalCalls > 0 || r.totalTokens > 0) {
      customerUsageMap.set(r.routeKey, r);
    }
  });

  const deprecatedRouteKeys = new Set(
    displayRoutes
      .filter((r) => DEPRECATED_ROUTE_KEYS.has(r.routeKey))
      .map((r) => r.routeKey)
  );
  const isDeprecated = (routeKey: string) => deprecatedRouteKeys.has(routeKey);

  const activeRoutes = displayRoutes.filter((r) => !isDeprecated(r.routeKey));
  const routesUsedByCustomer = activeRoutes.filter((r) =>
    customerUsageMap.has(r.routeKey)
  );
  const deprecatedRoutesUsedByCustomer = displayRoutes.filter(
    (r) => isDeprecated(r.routeKey) && customerUsageMap.has(r.routeKey)
  );
  const filteredRoutes = displayRoutes.filter((route) => {
    if (routeFilter === "used") {
      return customerUsageMap.has(route.routeKey);
    }
    if (routeFilter === "unused") {
      return !customerUsageMap.has(route.routeKey);
    }
    return true;
  });

  // Calculate date range for display
  const startDate = new Date(selectedAnalyticsRange.startUnix * 1000);
  const endDate = new Date(selectedAnalyticsRange.endUnix * 1000);
  const derivedDays = Math.max(
    1,
    Math.ceil(
      (selectedAnalyticsRange.endUnix - selectedAnalyticsRange.startUnix) /
        86400
    )
  );
  const days =
    customerStats?.daysAnalyzed || platformStats?.daysAnalyzed || derivedDays;
  const dateRangeStr = `${startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })} – ${endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
  const hoursInPeriod = days * 24;
  const platformTotalCalls = platformStats?.totalCalls || 0;
  const platformTotalTokens = platformStats?.totalTokens || 0;
  const platformTotalCustomers = platformStats?.totalCustomers || 0;
  const platformAvgDailyCalls = platformTotalCalls / days;
  const platformAvgHourlyCalls = platformTotalCalls / hoursInPeriod;
  const platformAvgDailyTokens = platformTotalTokens / days;
  const platformAvgDailyCallsPerCustomer =
    platformTotalCustomers > 0
      ? platformTotalCalls / days / platformTotalCustomers
      : 0;
  const customerTotalCalls = customerStats?.totalCalls || 0;
  const customerTotalTokens = customerStats?.totalTokens || 0;
  const customerAvgDailyCalls = customerTotalCalls / days;
  const customerAvgHourlyCalls = customerTotalCalls / hoursInPeriod;
  const customerAvgDailyTokens = customerTotalTokens / days;
  const customerAvgHourlyTokens = customerTotalTokens / hoursInPeriod;
  const usesBilledTokens =
    customerStats?.usesBilledTokens ?? packageMode === "platform";
  const billingModeLabel = usesBilledTokens ? "v2" : "v1";
  const billingModeDescription = usesBilledTokens
    ? "billed input + output tokens"
    : "raw tokens × route/model multipliers";
  const tokenMetricLabel = "Token Consumption";
  const tokenMetricDetailLabel = usesBilledTokens
    ? "billed input + output tokens"
    : "billable tokens after route/model multipliers";
  const includedTokensPerPeriodSafe = Math.max(0, includedTokensPerPeriod || 0);
  const projectedMonthlyTokens = customerAvgDailyTokens * 30;
  const useAuthoritativeBillingProjection =
    selectedAnalyticsRange.preset === "billing" &&
    typeof authoritativeProjectedPeriodTokens === "number" &&
    authoritativeProjectedPeriodTokens > 0;
  const projectedTokensForOverage = useAuthoritativeBillingProjection
    ? authoritativeProjectedPeriodTokens || 0
    : projectedMonthlyTokens;
  const projectedOverageTokens = Math.max(
    0,
    projectedTokensForOverage - includedTokensPerPeriodSafe
  );
  const isWithinIncludedTokens =
    includedTokensPerPeriodSafe > 0 && projectedOverageTokens <= 0;
  const projectedRefillOverageCost =
    includedTokensPerPeriodSafe > 0
      ? (projectedOverageTokens / 1000000) *
        (customerStats?.pricePerMillionTokens || 0)
      : customerStats?.insights?.projectedMonthlyCost || 0;
  const billingExample = customerStats?.billingExample;
  const billingExampleTokensForCost = billingExample
    ? usesBilledTokens
      ? billingExample.v2BilledTotalTokens
      : billingExample.v1BillableTokens
    : 0;
  const billingExampleIsAI =
    !!billingExample &&
    !["", "no-ai", "unknown"].includes(
      (billingExample.modelTag || "").trim().toLowerCase()
    );

  // Prepare chart data - Top 12 routes by CUSTOMER calls (not platform)
  const topCustomerRoutes = [...displayRoutes]
    .map((r) => ({
      ...r,
      customerData: customerUsageMap.get(r.routeKey),
    }))
    .filter((r) => (r.customerData?.totalCalls || 0) > 0) // Only routes with customer usage
    .sort(
      (a, b) =>
        (b.customerData?.totalCalls || 0) - (a.customerData?.totalCalls || 0)
    )
    .slice(0, 12);

  const chartData = {
    customerCalls: topCustomerRoutes.map(
      (r) => r.customerData?.totalCalls || 0
    ),
    customerTokens: topCustomerRoutes.map(
      (r) => r.customerData?.totalTokens || 0
    ),
    customerAvgDailyCalls: topCustomerRoutes.map(
      (r) => r.customerData?.avgDailyCalls || 0
    ),
    customerAvgHourlyCalls: topCustomerRoutes.map(
      (r) => r.customerData?.avgHourlyCalls || 0
    ),
    labelsFull: topCustomerRoutes.map((r) => r.displayName),
    labelsShort: topCustomerRoutes.map((r) =>
      r.displayName.length > 22
        ? `${r.displayName.slice(0, 22)}…`
        : r.displayName
    ),
    colors: topCustomerRoutes.map(
      (r) => getCategoryColor(r.category).plotColor
    ),
  };
  // Chart data is now customer-only (no platform comparison)

  return (
    <div className="space-y-6">
      {/* Customer Picker (Admin Only) */}
      {canViewCustomerPicker && (
        <Card className="p-4 bg-indigo-50 border-indigo-200">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-indigo-800">
              View as Customer:
            </span>
            <div className="flex-1 min-w-[300px] max-w-[500px]">
              <input
                className="w-full text-sm px-3 py-2 rounded-t-md border border-indigo-200 bg-white"
                placeholder="Type to search by name, email, or ID..."
                value={customerSearch}
                onChange={(event) => setCustomerSearch(event.target.value)}
              />
              <select
                className="w-full text-sm px-3 py-2 rounded-b-md border border-t-0 border-indigo-200 bg-white cursor-pointer"
                value={selectedCustomerId}
                onChange={(event) => setSelectedCustomerId(event.target.value)}
                disabled={isLoadingCustomers}
                size={1}
              >
                <option value="">
                  {isLoadingCustomers
                    ? "Loading customers..."
                    : customerFetchError
                    ? customerFetchError
                    : `-- Select a customer (${filteredCustomerOptions.length} available) --`}
                </option>
                {filteredCustomerOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                    {option.ownerEmail ? ` (${option.ownerEmail})` : ""} -{" "}
                    {option.id.slice(0, 8)}...
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleApplyCustomerOverride}
              disabled={!selectedCustomerId || isLoadingCustomers}
              className="text-sm px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Load Insights
            </button>
            {activeCustomerOverride && (
              <button
                type="button"
                onClick={handleClearCustomerOverride}
                className="text-sm px-3 py-2 rounded-md border border-indigo-300 text-indigo-600 hover:bg-indigo-100 font-medium"
              >
                Reset to My Account
              </button>
            )}
          </div>
          {activeCustomerOverride && (
            <p className="text-xs text-indigo-700 mt-2 font-medium bg-indigo-50 px-3 py-2 rounded-lg">
              Currently viewing:{" "}
              {customerOptions.find((c) => c.id === activeCustomerOverride)
                ?.name || activeCustomerOverride}
            </p>
          )}
          {customerFetchError && (
            <p className="text-xs text-red-600 mt-2">{customerFetchError}</p>
          )}
        </Card>
      )}

      {/* Toolbar: range, date, updated, refresh */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-gray-200 shadow-sm flex-nowrap overflow-x-auto">
        {/* Date Range Picker */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="insights-date-range"
            className="text-sm font-medium text-gray-700"
          >
            Period
          </label>
          <select
            id="insights-date-range"
            className="text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={selectedAnalyticsRange.preset}
            onChange={(event) => {
              const nextRange = buildRangeFromPreset(
                event.target.value as AnalyticsRangePreset,
                billingPeriodStartUnix,
                billingPeriodEndUnix
              );
              setSelectedAnalyticsRange((prev) =>
                prev.preset === nextRange.preset &&
                prev.startUnix === nextRange.startUnix &&
                prev.endUnix === nextRange.endUnix
                  ? prev
                  : nextRange
              );
            }}
          >
            <optgroup label="Billing Cycles">
              {ANALYTICS_RANGE_PRESETS.filter((p) => p.group === "billing").map(
                (preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                )
              )}
            </optgroup>
            <optgroup label="Calendar Ranges">
              {ANALYTICS_RANGE_PRESETS.filter(
                (p) => p.group === "calendar"
              ).map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full text-sm font-medium">
          <ClockIcon className="h-4 w-4" />
          <span>{dateRangeStr}</span>
          <span className="text-indigo-500">({days} days)</span>
        </div>
        {/* Last updated */}
        <span className="text-xs text-gray-600">
          Updated:{" "}
          {platformStats?.cachedAt
            ? new Date(platformStats.cachedAt).toLocaleTimeString()
            : "—"}
        </span>
        {/* Refresh button */}
        <button
          onClick={handleForceRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon
            className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
        {/* Admin-only: Force Clear Server Cache button */}
        {canViewCustomerPicker && (
          <Tooltip
            text="Admin only: Clears the server-side Redis cache and fetches fresh data from BigQuery. Use this if analytics data seems stale or incorrect."
            position="bottom"
            wide
          >
            <button
              onClick={handleClearCache}
              disabled={isRefreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              Clear Cache
            </button>
          </Tooltip>
        )}
      </div>

      {/* Platform-Wide Statistics - All Customers Combined */}
      {SHOW_PLATFORM_METRICS && (
        <Card className="p-5 bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-slate-600" />
              Platform-Wide Statistics
              <Tooltip
                text={`Combined usage across ALL ${platformTotalCustomers} customers on the platform over the last ${days} days. This shows overall platform activity, not your individual usage.`}
                position="right"
                wide
              >
                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-600" />
              </Tooltip>
            </h4>
            <span className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
              {platformTotalCustomers} total customers
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Total Calls */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <Tooltip
                text={`Total API calls made by ALL customers combined over ${days} days`}
                position="bottom"
              >
                <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                  Total Calls (All Customers)
                  <QuestionMarkCircleIcon className="h-3 w-3" />
                </p>
              </Tooltip>
              <p className="text-2xl font-bold text-slate-700">
                {formatNumber(platformTotalCalls)}
              </p>
              <p className="text-xs text-gray-600 mt-1">over {days} days</p>
            </div>
            {/* Avg Calls/Day (Platform Total) */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <Tooltip
                text={`Platform receives ${formatNumber(
                  platformAvgDailyCalls
                )} total API calls per day from all customers combined. That's ${formatNumber(
                  platformAvgHourlyCalls
                )} calls per hour.`}
                position="bottom"
                wide
              >
                <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                  Platform Calls/Day
                  <QuestionMarkCircleIcon className="h-3 w-3" />
                </p>
              </Tooltip>
              <p className="text-2xl font-bold text-slate-700">
                {formatNumber(platformAvgDailyCalls)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {formatNumber(platformAvgHourlyCalls)}/hr
              </p>
            </div>
            {/* Avg Per Customer/Day */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-100">
              <Tooltip
                text={`On average, each customer makes ${formatNumber(
                  platformAvgDailyCallsPerCustomer
                )} API calls per day. This helps you compare your usage to the typical customer.`}
                position="bottom"
                wide
              >
                <p className="text-xs font-medium text-blue-600 mb-1 flex items-center gap-1">
                  Avg Customer Calls/Day
                  <QuestionMarkCircleIcon className="h-3 w-3" />
                </p>
              </Tooltip>
              <p className="text-2xl font-bold text-blue-600">
                {formatNumber(platformAvgDailyCallsPerCustomer)}
              </p>
              <p className="text-xs text-gray-600 mt-1">per customer average</p>
            </div>
            {/* Total Tokens */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <Tooltip
                text={`Total tokens consumed by ALL customers combined over ${days} days. Daily average: ${formatNumber(
                  platformAvgDailyTokens
                )} tokens/day.`}
                position="bottom"
                wide
              >
                <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                  Total Tokens (All Customers)
                  <QuestionMarkCircleIcon className="h-3 w-3" />
                </p>
              </Tooltip>
              <p className="text-2xl font-bold text-slate-700">
                {formatNumber(platformTotalTokens)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {formatNumber(platformAvgDailyTokens)}/day
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* YOUR Account Summary - Clear separation from platform stats */}
      <Card className="p-5 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            Your Account Usage
            <Tooltip
              text={`Your personal usage statistics for the last ${days} days. This is YOUR data only - not platform-wide stats.`}
              position="right"
              wide
            >
              <QuestionMarkCircleIcon className="h-5 w-5 text-indigo-700" />
            </Tooltip>
          </h4>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs px-2 py-1 rounded-full bg-white border border-indigo-200 text-indigo-700 font-medium">
              Billing Mode: {billingModeLabel.toUpperCase()} (
              {billingModeDescription})
            </span>
            {includedTokensPerPeriodSafe > 0 && (
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium">
                Included this period:{" "}
                {formatNumber(includedTokensPerPeriodSafe)} tokens
              </span>
            )}

            {/* Comparison badge - only show when platform metrics enabled */}
            {SHOW_PLATFORM_METRICS &&
              platformAvgDailyCallsPerCustomer > 0 &&
              customerAvgDailyCalls > 0 && (
                <span
                  className={`text-xs px-2 py-1 rounded-full font-medium ${
                    customerAvgDailyCalls > platformAvgDailyCallsPerCustomer
                      ? "bg-green-100 text-green-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {customerAvgDailyCalls > platformAvgDailyCallsPerCustomer
                    ? `${(
                        (customerAvgDailyCalls /
                          platformAvgDailyCallsPerCustomer -
                          1) *
                        100
                      ).toFixed(0)}% above avg customer`
                    : `${(
                        (1 -
                          customerAvgDailyCalls /
                            platformAvgDailyCallsPerCustomer) *
                        100
                      ).toFixed(0)}% below avg customer`}
                </span>
              )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Your Total Calls */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-indigo-100">
            <Tooltip
              text={`YOUR total API calls over ${days} days.${
                SHOW_PLATFORM_METRICS
                  ? `\n\nCompare: Avg customer makes ~${formatNumber(
                      platformAvgDailyCallsPerCustomer * days
                    )} calls in this period.`
                  : ""
              }`}
              position="bottom"
              wide
            >
              <p className="text-sm font-medium text-indigo-600 mb-1 flex items-center gap-1">
                Your Total Calls
                <QuestionMarkCircleIcon className="h-3 w-3" />
              </p>
            </Tooltip>
            <p className="text-2xl font-bold text-indigo-600">
              {formatNumber(customerStats?.totalCalls || 0)}
            </p>
            <p className="text-sm text-gray-600 mt-1">over {days} days</p>
          </div>
          {/* Your Calls/Day */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-indigo-100">
            <Tooltip
              text={`YOU make ${formatNumber(
                customerAvgDailyCalls
              )} API calls per day on average.${
                SHOW_PLATFORM_METRICS
                  ? `\n\nPlatform avg per customer: ${formatNumber(
                      platformAvgDailyCallsPerCustomer
                    )}/day`
                  : ""
              }`}
              position="bottom"
              wide
            >
              <p className="text-sm font-medium text-indigo-600 mb-1 flex items-center gap-1">
                Your Calls/Day
                <QuestionMarkCircleIcon className="h-3 w-3" />
              </p>
            </Tooltip>
            <p className="text-2xl font-bold text-indigo-600">
              {formatNumber(customerAvgDailyCalls)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {formatNumber(customerAvgHourlyCalls)}/hr
            </p>
          </div>
          {/* Your Total Tokens */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-100">
            <Tooltip
              text={`YOUR total ${tokenMetricDetailLabel} over ${days} days.\n\nThis is a usage metric for the selected range, not automatic overage billing. Included monthly tokens are applied before refill charges.`}
              position="bottom"
              wide
            >
              <p className="text-sm font-medium text-purple-600 mb-1 flex items-center gap-1">
                Your Total {tokenMetricLabel}
                <QuestionMarkCircleIcon className="h-3 w-3" />
              </p>
            </Tooltip>
            <p className="text-2xl font-bold text-purple-600">
              {formatNumber(customerTotalTokens)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {formatNumber(customerTotalTokens / days)}/day
            </p>
          </div>
          {/* Projected Additional Refill Cost */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100">
            <Tooltip
              text={`Estimated additional refill cost you may need this billing period, beyond what you already have.

Your current token budget: ${formatNumber(
                includedTokensPerPeriodSafe
              )} tokens (plan included + any refills already purchased).

This projection subtracts your full budget (including refills you've already bought) from projected usage. It shows only the additional cost, not past refill charges.`}
              position="bottom"
              wide
            >
              <p className="text-sm font-medium text-green-600 mb-1 flex items-center gap-1">
                Est. Additional Refill Cost
                <QuestionMarkCircleIcon className="h-3 w-3" />
              </p>
            </Tooltip>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(projectedRefillOverageCost)}
            </p>
            {includedTokensPerPeriodSafe > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Projected usage: {formatNumber(projectedTokensForOverage)}{" "}
                tokens vs budget: {formatNumber(includedTokensPerPeriodSafe)}{" "}
                (plan + refills).
              </p>
            )}
            {isWithinIncludedTokens ? (
              <p className="text-sm text-emerald-600 mt-1 font-medium">
                Within your current budget — no additional refills projected
              </p>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                Additional refills may be needed beyond current budget
              </p>
            )}
            <div className="mt-2 pt-2 border-t border-green-100 text-xs text-gray-500">
              <p>
                Reference usage value:{" "}
                {customerStats?.totalCostFormatted || "$0.00"} over {days} days.
              </p>
              <p>
                Already-purchased refills are included in your budget above.
              </p>
            </div>
          </div>
          {/* Your Rate */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-100">
            <Tooltip
              text={
                customerStats?.priceIsEstimated
                  ? "We couldn't fetch your actual Stripe pricing. This is an estimated rate."
                  : "Your pricing tier based on your Stripe subscription. This determines cost per million tokens."
              }
              position="bottom"
              wide
            >
              <p className="text-sm font-medium text-blue-600 mb-1 flex items-center gap-1">
                Your Token Rate
                <QuestionMarkCircleIcon className="h-3 w-3" />
              </p>
            </Tooltip>
            <p className="text-2xl font-bold text-blue-600">
              ${customerStats?.pricePerMillionTokens?.toFixed(2) || "2.50"}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              per million tokens
              {customerStats?.priceIsEstimated && (
                <span className="text-amber-500 ml-1">(estimated)</span>
              )}
            </p>
          </div>
        </div>

        {/* ================================================================
            NEW BILLING SYSTEM BREAKDOWN (v2)
            Shows detailed input/output token breakdown when enabled
            ================================================================ */}
        {USE_NEW_BILLING_DISPLAY && customerStats?.hasNewBillingData && (
          <div className="mt-4 pt-4 border-t border-indigo-100">
            <div className="flex items-center gap-2 mb-3">
              <SparklesIcon className="h-4 w-4 text-amber-500" />
              <p className="text-sm font-semibold text-gray-700">
                Detailed Token Breakdown
              </p>
              <Tooltip
                text={
                  "New billing system showing input/output token breakdown.\n\n" +
                  "• Raw tokens: What the AI provider reports\n" +
                  `• Converted tokens: Normalized to $${
                    customerStats.housePricePerMTok?.toFixed(2) || "1.50"
                  }/million rate\n` +
                  `• Billed tokens: After ${(
                    (customerStats.inputMarkupRate || 0.33) * 100
                  ).toFixed(0)}% markup`
                }
                position="right"
                wide
              >
                <QuestionMarkCircleIcon className="h-3.5 w-3.5 text-gray-600" />
              </Tooltip>
            </div>

            {/* Token Flow Visualization */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Raw Tokens from Provider */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
                  <Tooltip
                    text="Raw tokens reported by the AI provider before any normalization"
                    position="top"
                  >
                    <span className="flex items-center gap-1">
                      Raw Provider Tokens
                      <QuestionMarkCircleIcon className="h-3 w-3" />
                    </span>
                  </Tooltip>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-slate-500">Input</p>
                    <p className="text-lg font-bold text-slate-700">
                      {formatNumber(customerStats.totalRawInputTokens || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Output</p>
                    <p className="text-lg font-bold text-slate-700">
                      {formatNumber(customerStats.totalRawOutputTokens || 0)}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Total:{" "}
                  {formatNumber(
                    (customerStats.totalRawInputTokens || 0) +
                      (customerStats.totalRawOutputTokens || 0)
                  )}
                </p>
              </div>

              {/* Converted Tokens (normalized) */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-3 border border-indigo-200">
                <p className="text-xs font-medium text-indigo-600 mb-2 flex items-center gap-1">
                  <Tooltip
                    text={
                      `Tokens normalized to $${
                        customerStats.housePricePerMTok?.toFixed(2) || "1.50"
                      }/million house rate using provider-specific multipliers.\n\n` +
                      `Input multiplier: ${
                        customerStats.avgInputPriceMultiplier?.toFixed(4) ||
                        "N/A"
                      }\n` +
                      `Output multiplier: ${
                        customerStats.avgOutputPriceMultiplier?.toFixed(4) ||
                        "N/A"
                      }`
                    }
                    position="top"
                    wide
                  >
                    <span className="flex items-center gap-1">
                      Normalized Tokens
                      <QuestionMarkCircleIcon className="h-3 w-3" />
                    </span>
                  </Tooltip>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-indigo-500">Input</p>
                    <p className="text-lg font-bold text-indigo-700">
                      {formatNumber(
                        customerStats.totalConvertedInputTokens || 0
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-indigo-500">Output</p>
                    <p className="text-lg font-bold text-indigo-700">
                      {formatNumber(
                        customerStats.totalConvertedOutputTokens || 0
                      )}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-indigo-400 mt-1">
                  Total:{" "}
                  {formatNumber(
                    (customerStats.totalConvertedInputTokens || 0) +
                      (customerStats.totalConvertedOutputTokens || 0)
                  )}
                </p>
              </div>

              {/* Billed Tokens (after markup) */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 border border-amber-200">
                <p className="text-xs font-medium text-amber-600 mb-2 flex items-center gap-1">
                  <Tooltip
                    text={
                      `Final billed tokens after ${(
                        (customerStats.inputMarkupRate || 0.33) * 100
                      ).toFixed(0)}% markup.\n\n` +
                      `These tokens are billed at $${
                        customerStats.housePricePerMTok?.toFixed(2) || "1.50"
                      } per million.\n` +
                      "This is your actual usage for billing purposes."
                    }
                    position="top"
                    wide
                  >
                    <span className="flex items-center gap-1">
                      Billed Tokens (Future)
                      <QuestionMarkCircleIcon className="h-3 w-3" />
                    </span>
                  </Tooltip>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-amber-500">Input</p>
                    <p className="text-lg font-bold text-amber-700">
                      {formatNumber(customerStats.totalBilledInputTokens || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-amber-500">Output</p>
                    <p className="text-lg font-bold text-amber-700">
                      {formatNumber(customerStats.totalBilledOutputTokens || 0)}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-amber-600 font-medium mt-1">
                  Total: {formatNumber(customerStats.totalBilledTokens || 0)}
                </p>
              </div>
            </div>

            {/* Cost Breakdown - Visual Flow */}
            <div className="mt-3 bg-gradient-to-r from-slate-50 via-amber-50/30 to-green-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between gap-2">
                {/* Provider Cost */}
                <div className="flex-1 text-center">
                  <Tooltip
                    text="What Passio pays to AI providers for your usage. This is the raw provider cost."
                    position="top"
                    wide
                  >
                    <p className="text-xs font-medium text-slate-500 mb-1 flex items-center justify-center gap-1">
                      Provider Cost
                      <QuestionMarkCircleIcon className="h-3 w-3" />
                    </p>
                  </Tooltip>
                  <p className="text-xl font-bold text-slate-700">
                    {formatCurrency(customerStats.totalWholesaleCostUSD || 0)}
                  </p>
                </div>

                {/* Arrow + Markup Badge */}
                <div className="flex flex-col items-center px-2">
                  <div className="flex items-center">
                    <div className="w-8 h-0.5 bg-gradient-to-r from-slate-300 to-amber-400" />
                    <div className="relative">
                      <div className="bg-gradient-to-br from-amber-400 to-amber-500 text-white px-3 py-1.5 rounded-full shadow-md">
                        <span className="text-sm font-bold">
                          +
                          {(
                            (customerStats.inputMarkupRate || 0.33) * 100
                          ).toFixed(0)}
                          %
                        </span>
                      </div>
                    </div>
                    <div className="w-8 h-0.5 bg-gradient-to-r from-amber-400 to-green-400" />
                  </div>
                  <Tooltip
                    text={
                      "Platform fee covers infrastructure, API management, " +
                      "support, and service costs."
                    }
                    position="bottom"
                  >
                    <p className="text-xs text-amber-600 mt-1 cursor-help">
                      Platform Fee
                    </p>
                  </Tooltip>
                </div>

                {/* Your Cost */}
                <div className="flex-1 text-center">
                  <Tooltip
                    text="Your total cost including provider cost plus Passio platform fees."
                    position="top"
                    wide
                  >
                    <p className="text-xs font-medium text-green-600 mb-1 flex items-center justify-center gap-1">
                      Your Total Cost
                      <QuestionMarkCircleIcon className="h-3 w-3" />
                    </p>
                  </Tooltip>
                  <p className="text-xl font-bold text-green-700">
                    {formatCurrency(customerStats.totalEstimatedCostUSD || 0)}
                  </p>
                </div>
              </div>

              {/* Savings indicator if there's data */}
              {(customerStats.totalProfitMarginUSD || 0) > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200/50 text-center">
                  <p className="text-xs text-gray-500">
                    Platform margin:{" "}
                    <span className="font-medium text-amber-600">
                      {formatCurrency(customerStats.totalProfitMarginUSD || 0)}
                    </span>
                    <span className="text-gray-600 ml-1">
                      ({" "}
                      {(
                        ((customerStats.totalProfitMarginUSD || 0) /
                          (customerStats.totalEstimatedCostUSD || 1)) *
                        100
                      ).toFixed(1)}
                      % of your cost)
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {billingExample && (
          <div className="mt-4 pt-4 border-t border-indigo-100">
            <div className="rounded-xl border border-indigo-200 bg-white p-4">
              <button
                type="button"
                onClick={() => setShowBillingWalkthrough((prev) => !prev)}
                className="w-full flex items-center justify-between gap-3 text-left"
                aria-expanded={showBillingWalkthrough}
              >
                <div>
                  <h5 className="text-sm font-semibold text-indigo-700">
                    How Your Cost Estimate Is Calculated (Sample Request)
                  </h5>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Uses one recent request row and applies your current tier
                    rate.
                  </p>
                </div>
                <div className="inline-flex items-center gap-2">
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {billingExample.billingMode.toUpperCase()} formula
                  </span>
                  {showBillingWalkthrough ? (
                    <ChevronUpIcon className="h-5 w-5 text-indigo-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-indigo-500" />
                  )}
                </div>
              </button>

              {showBillingWalkthrough && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">
                    Source row:{" "}
                    {new Date(billingExample.eventTime).toLocaleString()} •
                    model{" "}
                    <span className="font-mono">{billingExample.modelTag}</span>
                  </p>
                  {!billingExampleIsAI && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mb-2">
                      No AI-model row was found in this range, so this example
                      falls back to the latest non-AI request.
                    </p>
                  )}
                  <p
                    className="text-xs text-gray-500 mb-3 truncate"
                    title={billingExample.requestPath}
                  >
                    Route: {billingExample.requestPath}
                  </p>

                  {usesBilledTokens ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                        <p className="text-xs text-slate-500">
                          1. Raw input/output tokens
                        </p>
                        <p className="text-sm font-semibold text-slate-700">
                          {formatExactNumber(billingExample.rawInputTokens, 2)}{" "}
                          in +{" "}
                          {formatExactNumber(billingExample.rawOutputTokens, 2)}{" "}
                          out
                          {" = "}
                          {formatExactNumber(
                            billingExample.v2RawTotalTokens,
                            2
                          )}
                        </p>
                      </div>
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2">
                        <p className="text-xs text-indigo-500">
                          2. Normalized internal tokens
                        </p>
                        <p className="text-sm font-semibold text-indigo-700">
                          {formatExactNumber(
                            billingExample.convertedInputTokens,
                            2
                          )}
                          {" + "}
                          {formatExactNumber(
                            billingExample.convertedOutputTokens,
                            2
                          )}
                          {" = "}
                          {formatExactNumber(
                            billingExample.v2ConvertedTotalTokens,
                            2
                          )}
                        </p>
                      </div>
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2">
                        <p className="text-xs text-amber-600">
                          3. Billed tokens (v2)
                        </p>
                        <p className="text-sm font-semibold text-amber-700">
                          {formatExactNumber(
                            billingExample.billedInputTokens,
                            2
                          )}{" "}
                          +{" "}
                          {formatExactNumber(
                            billingExample.billedOutputTokens,
                            2
                          )}
                          {" = "}
                          {formatExactNumber(
                            billingExample.v2BilledTotalTokens,
                            2
                          )}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                        <p className="text-xs text-slate-500">1. Raw tokens</p>
                        <p className="text-sm font-semibold text-slate-700">
                          {formatExactNumber(billingExample.rawTokensUsed, 2)}
                        </p>
                      </div>
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2">
                        <p className="text-xs text-indigo-500">
                          2. Route/model multiplier
                        </p>
                        <p className="text-sm font-semibold text-indigo-700">
                          {formatExactNumber(billingExample.multiplier, 4)}x
                        </p>
                      </div>
                      <div className="rounded-lg border border-purple-200 bg-purple-50 p-2">
                        <p className="text-xs text-purple-600">
                          3. Billable tokens (v1)
                        </p>
                        <p className="text-sm font-semibold text-purple-700">
                          {formatExactNumber(billingExample.rawTokensUsed, 2)} x{" "}
                          {formatExactNumber(billingExample.multiplier, 4)} ={" "}
                          {formatExactNumber(
                            billingExample.v1BillableTokens,
                            2
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-2">
                    <p className="text-xs text-green-700">
                      4. Cost at your tier rate
                    </p>
                    <p className="text-sm font-semibold text-green-800">
                      ({formatExactNumber(billingExampleTokensForCost, 2)} /
                      1,000,000)
                      {" x $"}
                      {formatExactNumber(
                        billingExample.pricePerMillionTokens,
                        2
                      )}
                      {" = "}
                      {formatCurrency(billingExample.calculatedCostUsd)}
                    </p>
                    {billingExample.estimatedCostUsd > 0 && (
                      <p className="text-xs text-green-700 mt-1">
                        Row estimated cost (for reference):{" "}
                        {formatCurrency(billingExample.estimatedCostUsd)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5 bg-white border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-indigo-600" />
            <h4 className="font-semibold text-gray-900">API Routes Used</h4>
            <Tooltip
              text={`You've used ${routesUsedByCustomer.length} of ${
                activeRoutes.length
              } active routes${
                deprecatedRoutesUsedByCustomer.length > 0
                  ? `, plus ${
                      deprecatedRoutesUsedByCustomer.length
                    } deprecated route${
                      deprecatedRoutesUsedByCustomer.length > 1 ? "s" : ""
                    }`
                  : ""
              }.`}
              position="right"
              wide
            >
              <QuestionMarkCircleIcon className="h-5 w-5 text-indigo-700" />
            </Tooltip>
          </div>
          <div className="text-right">
            <span className="text-sm font-semibold text-indigo-700">
              {routesUsedByCustomer.length} of {activeRoutes.length}
            </span>
            {deprecatedRoutesUsedByCustomer.length > 0 && (
              <span className="text-sm text-amber-600 ml-2">
                +{deprecatedRoutesUsedByCustomer.length} deprecated
              </span>
            )}
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 flex overflow-hidden">
          <div
            className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 transition-all"
            style={{
              width: `${
                activeRoutes.length > 0
                  ? (routesUsedByCustomer.length / activeRoutes.length) * 100
                  : 0
              }%`,
            }}
          />
          {deprecatedRoutesUsedByCustomer.length > 0 && (
            <div
              className="bg-gradient-to-r from-amber-400 to-amber-500 h-3 transition-all"
              style={{
                width: `${
                  activeRoutes.length > 0
                    ? (deprecatedRoutesUsedByCustomer.length /
                        activeRoutes.length) *
                      100
                    : 0
                }%`,
              }}
            />
          )}
        </div>
        <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
          <span>
            {Math.max(0, activeRoutes.length - routesUsedByCustomer.length)}{" "}
            routes left to explore
          </span>
          {deprecatedRoutesUsedByCustomer.length > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400" />
              {deprecatedRoutesUsedByCustomer.length} deprecated
            </span>
          )}
        </div>
      </Card>

      {/* Platform Health Metrics - shows uptime/success rates from monitoring */}
      {SHOW_PLATFORM_METRICS &&
        (() => {
          // Calculate aggregate uptime metrics from routes that have data
          const routesWithUptime = displayRoutes.filter(
            (r) => r.uptimeTotalCalls > 0
          );

          const totalUptimeCalls = routesWithUptime.reduce(
            (sum, r) => sum + r.uptimeTotalCalls,
            0
          );
          const totalUptimeSuccess = routesWithUptime.reduce(
            (sum, r) => sum + r.uptimeSuccessCalls,
            0
          );
          const totalUptimeErrors = routesWithUptime.reduce(
            (sum, r) => sum + r.uptimeErrorCalls,
            0
          );
          const overallSuccessRate =
            totalUptimeCalls > 0
              ? (totalUptimeSuccess / totalUptimeCalls) * 100
              : 0;
          // Calculate actual platform-wide calls per day from monitoring data
          // This is the total monitored calls divided by the time period
          const uptimeWindowDays =
            UPTIME_WINDOW_DAYS[uptimeWindow] || UPTIME_WINDOW_DAYS["15m"];
          const uptimeWindowLabel =
            UPTIME_WINDOW_OPTIONS.find(
              (option) => option.value === uptimeWindow
            )?.label || "selected window";
          const monitoringCallsPerDay =
            uptimeWindowDays > 0 ? totalUptimeCalls / uptimeWindowDays : 0;

          const hasUptimeData = routesWithUptime.length > 0;

          return (
            <Card className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <svg
                    className="h-5 w-5 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Platform Health & Reliability
                  <Tooltip
                    text={`PLATFORM-WIDE API HEALTH\n\nThese metrics come from GCP Cloud Monitoring and show the health of the entire platform - ALL customers, ALL routes combined.\n\nUptime window: ${uptimeWindowLabel}\n\nThis is NOT your personal data - it shows overall platform reliability that affects everyone.`}
                    position="right"
                    wide
                  >
                    <QuestionMarkCircleIcon className="h-4 w-4 text-gray-600" />
                  </Tooltip>
                </h4>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-green-600 bg-green-100 px-2 py-1 rounded-full">
                    {hasUptimeData
                      ? `${routesWithUptime.length} routes tracked`
                      : "No data yet"}
                  </span>
                  <span className="text-gray-600 flex items-center gap-1">
                    <ClockIcon className="h-3.5 w-3.5" />
                    Uptime window
                  </span>
                  <select
                    className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white"
                    value={uptimeWindow}
                    onChange={(event) => setUptimeWindow(event.target.value)}
                  >
                    {UPTIME_WINDOW_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {hasUptimeData ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {/* Success Rate - larger, prominent */}
                  <Tooltip
                    text={`PLATFORM SUCCESS RATE\n\n${formatNumber(
                      totalUptimeSuccess
                    )} successful calls\n${formatNumber(
                      totalUptimeErrors
                    )} errors\n${formatNumber(
                      totalUptimeCalls
                    )} total\n\nThis measures API reliability across ALL customers and routes tracked by GCP Cloud Monitoring.`}
                    position="bottom"
                    wide
                  >
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100 relative overflow-hidden">
                      <div
                        className={`absolute inset-0 opacity-10 ${
                          overallSuccessRate >= 99
                            ? "bg-green-500"
                            : overallSuccessRate >= 95
                            ? "bg-yellow-500"
                            : "bg-red-500"
                        }`}
                      />
                      <div className="relative">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <CheckCircleIcon
                            className={`h-4 w-4 ${
                              overallSuccessRate >= 99
                                ? "text-green-600"
                                : overallSuccessRate >= 95
                                ? "text-yellow-600"
                                : "text-red-600"
                            }`}
                          />
                          <p className="text-xs font-medium text-gray-600">
                            Platform Success Rate
                          </p>
                          <QuestionMarkCircleIcon className="h-3 w-3 text-gray-600" />
                        </div>
                        <p
                          className={`text-3xl font-bold ${
                            overallSuccessRate >= 99
                              ? "text-green-600"
                              : overallSuccessRate >= 95
                              ? "text-yellow-600"
                              : "text-red-600"
                          }`}
                        >
                          {overallSuccessRate.toFixed(2)}
                          <span className="text-lg">%</span>
                        </p>
                      </div>
                    </div>
                  </Tooltip>
                  {/* Total Calls */}
                  <Tooltip
                    text={`TOTAL MONITORED CALLS\n\n${formatNumber(
                      totalUptimeCalls
                    )} API calls tracked by GCP Cloud Monitoring across all routes and all customers over the monitoring period.`}
                    position="bottom"
                    wide
                  >
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <ChartBarIcon className="h-4 w-4 text-gray-500" />
                        <p className="text-xs font-medium text-gray-600">
                          Total Monitored
                        </p>
                        <QuestionMarkCircleIcon className="h-3 w-3 text-gray-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-800">
                        {renderNumberWithSuffix(totalUptimeCalls)}
                      </p>
                    </div>
                  </Tooltip>
                  {/* Successful */}
                  <Tooltip
                    text={`SUCCESSFUL CALLS\n\n${formatNumber(
                      totalUptimeSuccess
                    )} API calls completed without errors (HTTP 2xx responses).`}
                    position="bottom"
                    wide
                  >
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-green-100">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        <p className="text-xs font-medium text-gray-600">
                          Successful
                        </p>
                        <QuestionMarkCircleIcon className="h-3 w-3 text-gray-600" />
                      </div>
                      <p className="text-2xl font-bold text-green-600">
                        {renderNumberWithSuffix(totalUptimeSuccess)}
                      </p>
                    </div>
                  </Tooltip>
                  {/* Errors */}
                  <Tooltip
                    text={`FAILED CALLS\n\n${formatNumber(
                      totalUptimeErrors
                    )} API calls that returned errors (HTTP 4xx/5xx). Check individual routes for details.`}
                    position="bottom"
                    wide
                  >
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-red-100">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <XCircleIcon className="h-4 w-4 text-red-500" />
                        <p className="text-xs font-medium text-gray-600">
                          Errors
                        </p>
                        <QuestionMarkCircleIcon className="h-3 w-3 text-gray-600" />
                      </div>
                      <p className="text-2xl font-bold text-red-600">
                        {renderNumberWithSuffix(totalUptimeErrors)}
                      </p>
                    </div>
                  </Tooltip>
                  {/* Platform Calls/Day from Monitoring */}
                  <Tooltip
                    text={`PLATFORM DAILY VOLUME (from Monitoring)\n\nTotal monitored calls: ${formatNumber(
                      totalUptimeCalls
                    )}\nDivided by ${uptimeWindowLabel} = ${formatNumber(
                      monitoringCallsPerDay
                    )}/day\n\nThis is the daily call volume across ALL customers tracked by GCP Cloud Monitoring.`}
                    position="bottom"
                    wide
                  >
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-blue-100">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <ClockIcon className="h-4 w-4 text-blue-500" />
                        <p className="text-xs font-medium text-gray-600">
                          Platform Calls/Day
                        </p>
                        <QuestionMarkCircleIcon className="h-3 w-3 text-gray-600" />
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {renderNumberWithSuffix(monitoringCallsPerDay)}
                      </p>
                    </div>
                  </Tooltip>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <p className="mb-2">
                    Monitoring metrics are not yet configured or no data
                    available.
                  </p>
                  <p className="text-xs">
                    Once GCP Cloud Monitoring is set up with success/error
                    log-based metrics, uptime and reliability stats will appear
                    here.
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3 text-center">
                Metrics from GCP Cloud Monitoring • Refreshes every 10 minutes
              </p>
            </Card>
          );
        })()}

      {/* Usage Insights & Trends */}
      {customerStats?.insights && (
        <Card className="p-5 bg-white border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-gray-600" />
              Usage Trends & Insights
            </h4>
            <button
              type="button"
              onClick={() => setShowTrends((prev) => !prev)}
              className="text-gray-500 hover:text-gray-700"
              aria-label={
                showTrends ? "Collapse trends section" : "Expand trends section"
              }
            >
              {showTrends ? (
                <ChevronUpIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          {showTrends && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {/* Trends */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Trends (vs. last 30 days)
                </p>
                <TrendIndicator
                  trend={customerStats.insights.callsTrend}
                  label="API Calls"
                />
                <TrendIndicator
                  trend={customerStats.insights.tokensTrend}
                  label={tokenMetricLabel}
                />
                <TrendIndicator
                  trend={customerStats.insights.costTrend}
                  label="Token Value"
                />
              </div>

              {/* Usage Patterns */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Usage Patterns
                </p>
                {customerStats.insights.busiestDayOfWeek &&
                  customerStats.insights.busiestDayOfWeek !== "N/A" && (
                    <div className="flex items-center gap-2 text-sm">
                      <ClockIcon className="h-4 w-4 text-gray-600" />
                      <span className="text-gray-600">Busiest day:</span>
                      <span className="font-medium">
                        {customerStats.insights.busiestDayOfWeek}
                      </span>
                    </div>
                  )}
                {customerStats.insights.busiestHourOfDay >= 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <ClockIcon className="h-4 w-4 text-gray-600" />
                    <span className="text-gray-600">Peak hour:</span>
                    <span className="font-medium">
                      {customerStats.insights.busiestHourOfDay}:00
                    </span>
                  </div>
                )}
                {customerStats.insights.topRouteByVolume && (
                  <div className="flex items-center gap-2 text-sm">
                    <ChartBarIcon className="h-4 w-4 text-gray-600" />
                    <span className="text-gray-600">Top route:</span>
                    <span className="font-medium truncate max-w-[150px]">
                      {customerStats.insights.topRouteByVolume}
                    </span>
                  </div>
                )}
              </div>

              {/* Daily Averages */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Daily Averages
                </p>
                <div className="text-sm">
                  <span className="text-gray-600">Calls/day: </span>
                  <span className="font-medium">
                    {formatNumber(customerStats.insights.avgCallsPerDay)}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">
                    {tokenMetricLabel}/day:{" "}
                  </span>
                  <span className="font-medium">
                    {formatNumber(customerAvgDailyTokens)}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">
                    Token value/day (reference):{" "}
                  </span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(customerStats.insights.avgCostPerDay)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Models Used (Your Usage) */}
      {customerStats?.models && customerStats.models.length > 0 && (
        <Card className="p-5 bg-white border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-indigo-600" />
              <h4 className="font-semibold text-gray-900">
                Models Used ({customerStats.models.length})
              </h4>
              <Tooltip
                text={`Models used across ${days} days`}
                position="right"
              >
                <QuestionMarkCircleIcon className="h-5 w-5 text-indigo-700" />
              </Tooltip>
            </div>
            <button
              type="button"
              onClick={() => setShowModels((prev) => !prev)}
              className="text-gray-500 hover:text-gray-700"
              aria-label={
                showModels ? "Collapse models section" : "Expand models section"
              }
            >
              {showModels ? (
                <ChevronUpIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          {showModels && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {customerStats.models.slice(0, 9).map((model) => (
                <div
                  key={model.model}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {model.model}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatNumber(model.totalTokens)} tokens
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-indigo-600">
                      {formatNumber(model.totalCalls)}
                    </p>
                    <p className="text-xs text-gray-600">calls</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* AI-Powered Insights */}
      <Card className="p-5 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-emerald-200 relative">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h4 className="font-bold text-lg text-emerald-900">
              AI-Powered Usage Analysis
            </h4>
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
              BETA
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowAIInsights((prev) => !prev)}
            className="rounded-full bg-white/80 p-1 text-emerald-600 shadow-sm hover:bg-white"
            aria-label={
              showAIInsights ? "Collapse AI insights" : "Expand AI insights"
            }
          >
            {showAIInsights ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        {showAIInsights && (
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-1">
                <p className="text-sm text-emerald-800 mb-3">
                  Get personalized recommendations and insights about your API
                  usage patterns, powered by our AI analyst. Click the button
                  below to generate your analysis.
                </p>
                {!aiInsightsUnlocked && (
                  <p className="text-sm font-medium text-amber-700 mb-3">
                    {aiInsightsLockedTooltip}
                  </p>
                )}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (!aiInsightsUnlocked) {
                        if (onOpenUpgrade) {
                          onOpenUpgrade();
                          return;
                        }
                        error(aiInsightsLockedTooltip);
                        return;
                      }
                      fetchAIInsights();
                    }}
                    disabled={
                      aiInsightsUnlocked &&
                      (aiInsightsLoading || aiInsightsCooldownRemaining > 0)
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aiInsightsLoading ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        Analyzing...
                      </>
                    ) : aiInsightsCooldownRemaining > 0 ? (
                      <>
                        <ClockIcon className="h-4 w-4" />
                        Available in{" "}
                        {Math.ceil(aiInsightsCooldownRemaining / 60000)}m
                      </>
                    ) : !aiInsightsUnlocked ? (
                      <>
                        <XCircleIcon className="h-4 w-4" />
                        Upgrade to Pro
                      </>
                    ) : aiInsightsData ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4" />
                        Refresh Insights
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="h-4 w-4" />
                        Generate AI Insights
                      </>
                    )}
                  </button>
                  {aiInsightsLastUpdated && (
                    <span className="text-xs text-emerald-600">
                      Last updated:{" "}
                      {new Date(aiInsightsLastUpdated).toLocaleDateString()}{" "}
                      {new Date(aiInsightsLastUpdated).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Error display */}
            {aiInsightsError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700">
                  <XCircleIcon className="h-5 w-5" />
                  <span className="font-medium">Error:</span>
                  <span className="text-sm">{aiInsightsError}</span>
                </div>
              </div>
            )}

            {/* AI Insights Results - Collapsible Sections */}
            {aiInsightsUnlocked && aiInsightsData && (
              <div className="mt-6 space-y-3">
                {/* Summary Section */}
                {aiInsightsData.summary && (
                  <div className="bg-white rounded-xl border border-emerald-200 shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        const newSet = new Set(expandedInsightsSections);
                        newSet.has("summary")
                          ? newSet.delete("summary")
                          : newSet.add("summary");
                        setExpandedInsightsSections(newSet);
                      }}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                        <span className="font-semibold text-emerald-900">
                          Summary
                        </span>
                      </div>
                      {expandedInsightsSections.has("summary") ? (
                        <ChevronUpIcon className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-emerald-600" />
                      )}
                    </button>
                    {expandedInsightsSections.has("summary") && (
                      <div className="px-4 py-3 border-t border-emerald-100">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {aiInsightsData.summary}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Alerts Section */}
                {aiInsightsData.alerts && aiInsightsData.alerts.length > 0 && (
                  <div className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        const newSet = new Set(expandedInsightsSections);
                        newSet.has("alerts")
                          ? newSet.delete("alerts")
                          : newSet.add("alerts");
                        setExpandedInsightsSections(newSet);
                      }}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <XCircleIcon className="h-5 w-5 text-red-600" />
                        <span className="font-semibold text-red-900">
                          Alerts
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                          {aiInsightsData.alerts.length}
                        </span>
                      </div>
                      {expandedInsightsSections.has("alerts") ? (
                        <ChevronUpIcon className="h-5 w-5 text-red-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-red-600" />
                      )}
                    </button>
                    {expandedInsightsSections.has("alerts") && (
                      <div className="px-4 py-3 border-t border-red-100 space-y-3">
                        {aiInsightsData.alerts.map((alert, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg border-l-4 ${
                              alert.severity === "critical"
                                ? "bg-red-50 border-red-500"
                                : alert.severity === "warning"
                                ? "bg-amber-50 border-amber-500"
                                : "bg-blue-50 border-blue-500"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <span
                                className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${
                                  alert.severity === "critical"
                                    ? "bg-red-200 text-red-800"
                                    : alert.severity === "warning"
                                    ? "bg-amber-200 text-amber-800"
                                    : "bg-blue-200 text-blue-800"
                                }`}
                              >
                                {alert.severity}
                              </span>
                              <div className="flex-1">
                                <h6 className="font-semibold text-gray-900 mb-1">
                                  {alert.title}
                                </h6>
                                <p className="text-sm text-gray-700 mb-2">
                                  {alert.message}
                                </p>
                                {alert.context && (
                                  <p className="text-xs text-gray-500 italic">
                                    {alert.context}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Scores Section */}
                {aiInsightsData.scores && aiInsightsData.scores.length > 0 && (
                  <div className="bg-white rounded-xl border border-blue-200 shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        const newSet = new Set(expandedInsightsSections);
                        newSet.has("scores")
                          ? newSet.delete("scores")
                          : newSet.add("scores");
                        setExpandedInsightsSections(newSet);
                      }}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <ChartBarIcon className="h-5 w-5 text-blue-600" />
                        <span className="font-semibold text-blue-900">
                          Health Scores
                        </span>
                      </div>
                      {expandedInsightsSections.has("scores") ? (
                        <ChevronUpIcon className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-blue-600" />
                      )}
                    </button>
                    {expandedInsightsSections.has("scores") && (
                      <div className="px-4 py-4 border-t border-blue-100">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {aiInsightsData.scores.map((score, idx) => {
                            const maxValue = score.max_value || 10; // Default to 10 if not provided
                            const percentage = (score.value / maxValue) * 100;
                            const colorClass =
                              percentage >= 70
                                ? "bg-green-500"
                                : percentage >= 40
                                ? "bg-amber-500"
                                : "bg-red-500";
                            return (
                              <div
                                key={idx}
                                className="bg-gray-50 rounded-lg p-4"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-700 capitalize">
                                    {score.name.replace(/_/g, " ")}
                                  </span>
                                  <span className="text-lg font-bold text-gray-900">
                                    {score.value}/{maxValue}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                  <div
                                    className={`h-2.5 rounded-full ${colorClass}`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <p className="text-xs text-gray-500">
                                  {score.description}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Key Insights Section */}
                {aiInsightsData.key_insights &&
                  aiInsightsData.key_insights.length > 0 && (
                    <div className="bg-white rounded-xl border border-purple-200 shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          const newSet = new Set(expandedInsightsSections);
                          newSet.has("key_insights")
                            ? newSet.delete("key_insights")
                            : newSet.add("key_insights");
                          setExpandedInsightsSections(newSet);
                        }}
                        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <SparklesIcon className="h-5 w-5 text-purple-600" />
                          <span className="font-semibold text-purple-900">
                            Key Insights
                          </span>
                        </div>
                        {expandedInsightsSections.has("key_insights") ? (
                          <ChevronUpIcon className="h-5 w-5 text-purple-600" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-purple-600" />
                        )}
                      </button>
                      {expandedInsightsSections.has("key_insights") && (
                        <div className="px-4 py-3 border-t border-purple-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {aiInsightsData.key_insights.map((insight, idx) => (
                              <div
                                key={idx}
                                className="bg-purple-50 rounded-lg p-3"
                              >
                                <p className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-1">
                                  {insight.name.replace(/_/g, " ")}
                                </p>
                                <p className="text-lg font-semibold text-purple-900 mb-1">
                                  {insight.value}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {insight.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                {/* Recommendations Section */}
                {aiInsightsData.recommendations &&
                  aiInsightsData.recommendations.length > 0 && (
                    <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          const newSet = new Set(expandedInsightsSections);
                          newSet.has("recommendations")
                            ? newSet.delete("recommendations")
                            : newSet.add("recommendations");
                          setExpandedInsightsSections(newSet);
                        }}
                        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <ArrowTrendingUpIcon className="h-5 w-5 text-amber-600" />
                          <span className="font-semibold text-amber-900">
                            Recommendations
                          </span>
                          <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                            {aiInsightsData.recommendations.length}
                          </span>
                        </div>
                        {expandedInsightsSections.has("recommendations") ? (
                          <ChevronUpIcon className="h-5 w-5 text-amber-600" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-amber-600" />
                        )}
                      </button>
                      {expandedInsightsSections.has("recommendations") && (
                        <div className="px-4 py-3 border-t border-amber-100 space-y-4">
                          {aiInsightsData.recommendations.map((rec, idx) => (
                            <div
                              key={idx}
                              className={`p-4 rounded-lg border ${
                                rec.priority === "high"
                                  ? "bg-red-50 border-red-200"
                                  : rec.priority === "medium"
                                  ? "bg-amber-50 border-amber-200"
                                  : "bg-green-50 border-green-200"
                              }`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${
                                      rec.priority === "high"
                                        ? "bg-red-200 text-red-800"
                                        : rec.priority === "medium"
                                        ? "bg-amber-200 text-amber-800"
                                        : "bg-green-200 text-green-800"
                                    }`}
                                  >
                                    {rec.priority}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 text-xs font-medium rounded ${
                                      rec.type === "cost_savings"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : rec.type === "feature_discovery"
                                        ? "bg-blue-100 text-blue-700"
                                        : "bg-purple-100 text-purple-700"
                                    }`}
                                  >
                                    {rec.type.replace(/_/g, " ")}
                                  </span>
                                </div>
                                <span className="text-sm font-bold text-emerald-700">
                                  {rec.estimated_impact}
                                </span>
                              </div>
                              <h6 className="font-semibold text-gray-900 mb-1">
                                {rec.title}
                              </h6>
                              <p className="text-sm text-gray-600 mb-2">
                                {rec.route_name}
                              </p>
                              <p className="text-sm text-gray-700 mb-3">
                                {rec.description}
                              </p>
                              {(rec.current_model || rec.suggested_model) && (
                                <div className="flex items-center gap-2 text-sm bg-white/50 rounded p-2">
                                  {rec.current_model && (
                                    <span className="text-gray-500">
                                      Current:{" "}
                                      <strong>{rec.current_model}</strong>
                                    </span>
                                  )}
                                  {rec.current_model && rec.suggested_model && (
                                    <span className="text-gray-600">→</span>
                                  )}
                                  {rec.suggested_model && (
                                    <span className="text-emerald-700">
                                      Suggested:{" "}
                                      <strong>{rec.suggested_model}</strong>
                                    </span>
                                  )}
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-2 italic">
                                {rec.reasoning}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                {/* Unused Features Section */}
                {aiInsightsData.unused_features &&
                  aiInsightsData.unused_features.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          const newSet = new Set(expandedInsightsSections);
                          newSet.has("unused_features")
                            ? newSet.delete("unused_features")
                            : newSet.add("unused_features");
                          setExpandedInsightsSections(newSet);
                        }}
                        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-gray-50 to-slate-50 hover:from-gray-100 hover:to-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <MinusIcon className="h-5 w-5 text-gray-600" />
                          <span className="font-semibold text-gray-900">
                            Unused Features to Explore
                          </span>
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                            {aiInsightsData.unused_features.length}
                          </span>
                        </div>
                        {expandedInsightsSections.has("unused_features") ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                        )}
                      </button>
                      {expandedInsightsSections.has("unused_features") && (
                        <div className="px-4 py-3 border-t border-gray-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {aiInsightsData.unused_features.map(
                              (feature, idx) => (
                                <div
                                  key={idx}
                                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-500 uppercase">
                                      {feature.category}
                                    </span>
                                    <span
                                      className={`px-2 py-0.5 text-xs font-medium rounded ${
                                        feature.platform_popularity === "High"
                                          ? "bg-green-100 text-green-700"
                                          : feature.platform_popularity ===
                                            "Medium"
                                          ? "bg-amber-100 text-amber-700"
                                          : "bg-gray-100 text-gray-700"
                                      }`}
                                    >
                                      {feature.platform_popularity} popularity
                                    </span>
                                  </div>
                                  <h6 className="font-semibold text-gray-900 mb-2">
                                    {feature.route_name}
                                  </h6>
                                  <p className="text-sm text-gray-600">
                                    {feature.value_proposition}
                                  </p>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* All Routes Table - Simplified with sticky headers */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-gray-600" />
              <h4 className="font-semibold text-gray-900">All API Routes</h4>
              <span className="text-xs text-gray-500">
                ({displayRoutes.length} routes)
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-gray-600">
                <button
                  type="button"
                  onClick={() => setRouteFilter("all")}
                  className={`px-2 py-0.5 rounded-full transition-colors ${
                    routeFilter === "all"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setRouteFilter("used")}
                  className={`px-2 py-0.5 rounded-full transition-colors ${
                    routeFilter === "used"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Used
                </button>
                <button
                  type="button"
                  onClick={() => setRouteFilter("unused")}
                  className={`px-2 py-0.5 rounded-full transition-colors ${
                    routeFilter === "unused"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Unused
                </button>
              </div>
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircleIcon className="h-4 w-4" />
                You use ({routesUsedByCustomer.length})
              </span>
              <span className="flex items-center gap-1 text-amber-500">
                <XCircleIcon className="h-4 w-4" />
                Not using ({displayRoutes.length - routesUsedByCustomer.length})
              </span>
              <button
                type="button"
                onClick={() => setShowRoutesTable((prev) => !prev)}
                className="text-gray-500 hover:text-gray-700 ml-2"
                aria-label={
                  showRoutesTable
                    ? "Collapse routes table"
                    : "Expand routes table"
                }
              >
                {showRoutesTable ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Model Override Save Bar */}
        {!modelOverridesUnlocked && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
            {modelOverridesLockedTooltip}
          </div>
        )}

        {modelOverridesUnlocked && hasUnsavedChanges && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800">
              <ArrowPathIcon className="h-5 w-5" />
              <span className="font-medium">
                You have unsaved model preference changes
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={resetModelOverrides}
                disabled={isSavingOverrides}
                className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Reset
              </button>
              <button
                onClick={saveModelOverrides}
                disabled={isSavingOverrides}
                className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
              >
                {isSavingOverrides ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        )}

        {showRoutesTable && (
          <div className="overflow-auto max-h-[600px]">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                {/* Group Headers */}
                <tr className="border-b border-gray-200 bg-gray-100 text-xs">
                  <th className="py-2 px-4 text-left text-gray-500" colSpan={2}>
                    Route
                  </th>
                  {/* Platform group header - hidden when SHOW_PLATFORM_METRICS is false */}
                  {SHOW_PLATFORM_METRICS && (
                    <th
                      className="py-2 px-2 text-center text-gray-500 border-l border-gray-200"
                      colSpan={3}
                    >
                      Platform (All Customers)
                    </th>
                  )}
                  <th
                    className="py-2 px-2 text-center text-indigo-600 border-l border-gray-200"
                    colSpan={SHOW_PLATFORM_METRICS ? 5 : 4}
                  >
                    Your Usage
                  </th>
                  <th
                    className="py-2 px-2 text-center text-purple-600 border-l border-gray-200"
                    colSpan={1}
                  >
                    Settings
                  </th>
                </tr>
                {/* Column Headers */}
                <tr className="border-b-2 border-gray-200">
                  <th
                    className="text-left py-2.5 px-3 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors min-w-[280px]"
                    onClick={() => handleSort("displayName")}
                  >
                    <div className="flex items-center gap-1">
                      API Route
                      {sortField === "displayName" &&
                        (sortDir === "desc" ? (
                          <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                        ) : (
                          <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                        ))}
                    </div>
                  </th>
                  <th className="text-center py-2.5 px-1 font-semibold text-gray-500 w-12">
                    Docs
                  </th>
                  <th
                    className="text-left py-2.5 px-2 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort("category")}
                  >
                    <div className="flex items-center gap-1">
                      Type
                      {sortField === "category" &&
                        (sortDir === "desc" ? (
                          <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                        ) : (
                          <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                        ))}
                    </div>
                  </th>
                  {/* Platform columns - hidden when SHOW_PLATFORM_METRICS is false */}
                  {SHOW_PLATFORM_METRICS && (
                    <>
                      <th
                        className="text-right py-2.5 px-2 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap border-l border-gray-200"
                        onClick={() => handleSort("platformCalls")}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Total Calls
                          {sortField === "platformCalls" &&
                            (sortDir === "desc" ? (
                              <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                            ) : (
                              <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                            ))}
                        </div>
                      </th>
                      <th
                        className="text-right py-2.5 px-2 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                        onClick={() => handleSort("platformAvgPerCustomer")}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Avg/Cust/Day
                          {sortField === "platformAvgPerCustomer" &&
                            (sortDir === "desc" ? (
                              <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                            ) : (
                              <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                            ))}
                        </div>
                      </th>
                      <th
                        className="text-right py-2.5 px-2 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                        onClick={() => handleSort("platformCustomers")}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Customers
                          {sortField === "platformCustomers" &&
                            (sortDir === "desc" ? (
                              <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
                            ) : (
                              <ChevronUpIcon className="h-4 w-4 text-indigo-600" />
                            ))}
                        </div>
                      </th>
                    </>
                  )}
                  {/* Your Usage columns */}
                  <th
                    className="text-right py-2.5 px-2 font-semibold text-indigo-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap border-l border-gray-200"
                    onClick={() => handleSort("yourCalls")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Calls
                      {sortField === "yourCalls" &&
                        (sortDir === "desc" ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronUpIcon className="h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    className="text-right py-2.5 px-2 font-semibold text-indigo-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                    onClick={() => handleSort("yourCallsPerDay")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      /Day
                      {sortField === "yourCallsPerDay" &&
                        (sortDir === "desc" ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronUpIcon className="h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    className="text-right py-2.5 px-2 font-semibold text-indigo-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                    onClick={() => handleSort("yourTokens")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {tokenMetricLabel}
                      {sortField === "yourTokens" &&
                        (sortDir === "desc" ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronUpIcon className="h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    className="text-right py-2.5 px-2 font-semibold text-green-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                    onClick={() => handleSort("yourCost")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Cost
                      {sortField === "yourCost" &&
                        (sortDir === "desc" ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronUpIcon className="h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  {/* vs Avg column - hidden when SHOW_PLATFORM_METRICS is false */}
                  {SHOW_PLATFORM_METRICS && (
                    <th
                      className="text-right py-2.5 px-2 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                      onClick={() => handleSort("yourVsAvg")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        vs Avg
                        {sortField === "yourVsAvg" &&
                          (sortDir === "desc" ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronUpIcon className="h-4 w-4" />
                          ))}
                      </div>
                    </th>
                  )}
                  {/* Model Override */}
                  <th className="text-center py-2.5 px-2 font-semibold text-purple-600 whitespace-nowrap border-l border-gray-200 min-w-[140px]">
                    <Tooltip
                      text="Select a different AI model to use for this route. 'Passio Default' uses our recommended model for best value and accuracy."
                      position="left"
                      wide
                    >
                      <div className="flex items-center justify-center gap-1">
                        <SparklesIcon className="h-4 w-4" />
                        AI Model
                      </div>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {[...filteredRoutes]
                  .sort((a, b) => {
                    const customerA = customerUsageMap.get(a.routeKey);
                    const customerB = customerUsageMap.get(b.routeKey);
                    let valA: number | string = 0;
                    let valB: number | string = 0;
                    // Calculate platform avg per customer for sorting
                    const avgPerCustA =
                      a.uniqueCustomers > 0
                        ? a.totalCalls / days / a.uniqueCustomers
                        : 0;
                    const avgPerCustB =
                      b.uniqueCustomers > 0
                        ? b.totalCalls / days / b.uniqueCustomers
                        : 0;

                    if (sortField === "smartDefault") {
                      const isUsedA = !!customerA;
                      const isUsedB = !!customerB;
                      const isDeprecatedA = isDeprecated(a.routeKey);
                      const isDeprecatedB = isDeprecated(b.routeKey);
                      const isPopularA =
                        POPULAR_ROUTE_KEYS.has(a.routeKey) &&
                        !isUsedA &&
                        !isDeprecatedA;
                      const isPopularB =
                        POPULAR_ROUTE_KEYS.has(b.routeKey) &&
                        !isUsedB &&
                        !isDeprecatedB;
                      const groupA = isDeprecatedA
                        ? 3
                        : isUsedA
                        ? 0
                        : isPopularA
                        ? 1
                        : 2;
                      const groupB = isDeprecatedB
                        ? 3
                        : isUsedB
                        ? 0
                        : isPopularB
                        ? 1
                        : 2;
                      if (groupA !== groupB) return groupA - groupB;
                      if (isUsedA && isUsedB)
                        return (
                          (customerB?.totalCalls || 0) -
                          (customerA?.totalCalls || 0)
                        );
                      return b.totalCalls - a.totalCalls;
                    }

                    switch (sortField) {
                      case "displayName":
                        valA = a.displayName.toLowerCase();
                        valB = b.displayName.toLowerCase();
                        break;
                      case "category":
                        valA = a.category.toLowerCase();
                        valB = b.category.toLowerCase();
                        break;
                      case "platformCalls":
                        valA = a.totalCalls;
                        valB = b.totalCalls;
                        break;
                      case "platformAvgPerCustomer":
                        valA = avgPerCustA;
                        valB = avgPerCustB;
                        break;
                      case "platformCustomers":
                        valA = a.uniqueCustomers;
                        valB = b.uniqueCustomers;
                        break;
                      case "yourCalls":
                        valA = customerA?.totalCalls || 0;
                        valB = customerB?.totalCalls || 0;
                        break;
                      case "yourCallsPerDay":
                        valA = customerA?.avgDailyCalls || 0;
                        valB = customerB?.avgDailyCalls || 0;
                        break;
                      case "yourTokens":
                        valA = customerA?.totalTokens || 0;
                        valB = customerB?.totalTokens || 0;
                        break;
                      case "yourCost":
                        valA = customerA?.totalCostUSD || 0;
                        valB = customerB?.totalCostUSD || 0;
                        break;
                      case "yourVsAvg":
                        valA =
                          avgPerCustA > 0
                            ? ((customerA?.avgDailyCalls || 0) - avgPerCustA) /
                              avgPerCustA
                            : 0;
                        valB =
                          avgPerCustB > 0
                            ? ((customerB?.avgDailyCalls || 0) - avgPerCustB) /
                              avgPerCustB
                            : 0;
                        break;
                    }

                    if (typeof valA === "string") {
                      return sortDir === "asc"
                        ? valA.localeCompare(valB as string)
                        : (valB as string).localeCompare(valA);
                    }
                    return sortDir === "asc"
                      ? (valA as number) - (valB as number)
                      : (valB as number) - (valA as number);
                  })
                  .map((route) => {
                    const customerRoute = customerUsageMap.get(route.routeKey);
                    const isUsed = !!customerRoute;
                    const catColor = getCategoryColor(route.category);
                    // Platform avg per customer per day for THIS route
                    const platformAvgPerCustomerDay =
                      route.uniqueCustomers > 0
                        ? route.totalCalls / days / route.uniqueCustomers
                        : 0;
                    const hasUptimeData = route.uptimeTotalCalls > 0;
                    const uptimeRatePct = route.uptimeSuccessRate * 100;
                    const isPopular =
                      POPULAR_ROUTE_KEYS.has(route.routeKey) &&
                      !isDeprecated(route.routeKey);
                    const isOpportunity = isPopular && !isUsed;

                    // Calculate vs avg percentage
                    const yourDailyCalls = customerRoute?.avgDailyCalls || 0;
                    const vsAvgPct =
                      platformAvgPerCustomerDay > 0
                        ? ((yourDailyCalls - platformAvgPerCustomerDay) /
                            platformAvgPerCustomerDay) *
                          100
                        : 0;

                    const isExpanded = expandedRoutes.has(route.routeKey);
                    const routeIsDeprecated = isDeprecated(route.routeKey);

                    return (
                      <React.Fragment key={route.routeKey}>
                        <tr
                          className={`cursor-pointer transition-colors ${
                            routeIsDeprecated
                              ? "bg-gray-50/60 hover:bg-gray-100/60 opacity-75"
                              : isUsed
                              ? "bg-green-50/30 hover:bg-green-50/60"
                              : isOpportunity
                              ? "bg-amber-50/40 hover:bg-amber-50/70"
                              : "hover:bg-blue-50/50"
                          } ${isExpanded ? "border-b-0" : ""}`}
                          onClick={() => toggleRouteExpanded(route.routeKey)}
                          title="Click to expand details"
                        >
                          {/* Route Info */}
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              {/* Expand/collapse chevron */}
                              <button
                                className="p-0.5 rounded hover:bg-gray-200 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRouteExpanded(route.routeKey);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <ChevronUpIcon className="h-4 w-4 text-gray-600 rotate-90" />
                                )}
                              </button>
                              {isUsed ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                              ) : (
                                <XCircleIcon className="h-5 w-5 text-gray-300 flex-shrink-0" />
                              )}
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    {route.displayName}
                                  </span>
                                  {routeIsDeprecated && (
                                    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                                      Deprecated
                                    </span>
                                  )}
                                  {!isUsed && !routeIsDeprecated && (
                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                                      Not used
                                    </span>
                                  )}
                                  {isPopular && (
                                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                                      Popular
                                    </span>
                                  )}
                                </div>
                                <p
                                  className="text-xs text-gray-500 truncate max-w-[250px]"
                                  title={route.description}
                                >
                                  {route.description}
                                </p>
                                {ROUTE_NOTES[route.routeKey] && (
                                  <p className="text-xs text-indigo-600 mt-0.5 italic">
                                    {ROUTE_NOTES[route.routeKey]}
                                  </p>
                                )}
                                {/* Health indicator */}
                                {hasUptimeData && (
                                  <Tooltip
                                    text={`Platform Health\n\n• Uptime: ${uptimeRatePct.toFixed(
                                      1
                                    )}%\n• ${formatNumber(
                                      route.uptimeSuccessCalls
                                    )} successful\n• ${formatNumber(
                                      route.uptimeErrorCalls
                                    )} errors`}
                                    position="right"
                                    wide
                                  >
                                    <span
                                      className={`inline-flex items-center gap-1 mt-1 text-xs ${
                                        uptimeRatePct >= 99
                                          ? "text-green-600"
                                          : uptimeRatePct >= 95
                                          ? "text-yellow-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      <span
                                        className={`w-2 h-2 rounded-full ${
                                          uptimeRatePct >= 99
                                            ? "bg-green-500"
                                            : uptimeRatePct >= 95
                                            ? "bg-yellow-500"
                                            : "bg-red-500"
                                        }`}
                                      />
                                      {uptimeRatePct.toFixed(1)}% uptime
                                      {route.uptimeErrorCalls > 0 && (
                                        <span className="text-red-500">
                                          (
                                          {formatNumber(route.uptimeErrorCalls)}{" "}
                                          errors)
                                        </span>
                                      )}
                                    </span>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </td>
                          {/* Docs Link */}
                          <td className="py-3 px-1 text-center">
                            {route.docsUrl ? (
                              <a
                                href={route.docsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 transition-colors"
                                title="View API Documentation"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                              </a>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          {/* Category */}
                          <td className="py-3 px-2">
                            <span
                              className={`inline-flex text-xs px-2 py-1 rounded-full ${catColor.bg} ${catColor.text}`}
                            >
                              {route.category}
                            </span>
                          </td>
                          {/* Platform columns - hidden when SHOW_PLATFORM_METRICS is false */}
                          {SHOW_PLATFORM_METRICS && (
                            <>
                              {/* Platform: Total Calls */}
                              <td className="py-3 px-2 text-right text-gray-600 tabular-nums border-l border-gray-100">
                                {renderNumberWithSuffix(
                                  route.totalCalls,
                                  "font-medium"
                                )}
                              </td>
                              {/* Platform: Avg per Customer per Day */}
                              <td className="py-3 px-2 text-right text-gray-600 tabular-nums">
                                <Tooltip
                                  text={`Platform average:\n${formatNumber(
                                    platformAvgPerCustomerDay
                                  )}/day/customer\n${formatNumber(
                                    platformAvgPerCustomerDay / 24
                                  )}/hr/customer`}
                                  position="top"
                                >
                                  <span>
                                    {formatNumber(platformAvgPerCustomerDay)}
                                  </span>
                                </Tooltip>
                              </td>
                              {/* Platform: Customers */}
                              <td className="py-3 px-2 text-right text-gray-600 tabular-nums">
                                {route.uniqueCustomers}
                              </td>
                            </>
                          )}
                          {/* Your: Calls */}
                          <td className="py-3 px-2 text-right font-medium text-indigo-600 tabular-nums border-l border-gray-100">
                            {customerRoute ? (
                              formatNumber(customerRoute.totalCalls)
                            ) : (
                              <span className="text-gray-300 font-normal">
                                —
                              </span>
                            )}
                          </td>
                          {/* Your: Calls/Day */}
                          <td className="py-3 px-2 text-right text-indigo-600 tabular-nums">
                            {customerRoute ? (
                              <Tooltip
                                text={`Your usage:\n${formatNumber(
                                  customerRoute.avgDailyCalls
                                )}/day\n${formatNumber(
                                  customerRoute.avgHourlyCalls
                                )}/hr`}
                                position="top"
                              >
                                <span>
                                  {formatNumber(customerRoute.avgDailyCalls)}
                                </span>
                              </Tooltip>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          {/* Your: Tokens */}
                          <td className="py-3 px-2 text-right text-indigo-600 tabular-nums">
                            {customerRoute ? (
                              customerRoute.totalTokensFormatted
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          {/* Your: Cost */}
                          <td className="py-3 px-2 text-right font-medium text-green-600 tabular-nums">
                            {customerRoute ? (
                              <Tooltip
                                text={`Cost breakdown:\n• Total: ${
                                  customerRoute.totalCostFormatted
                                }\n• ${formatCurrency(
                                  customerRoute.avgDailyCostUSD
                                )}/day\n• ${formatCurrency(
                                  customerRoute.avgHourlyCostUSD
                                )}/hr\n• ${formatCurrency(
                                  customerRoute.costPerCall
                                )}/call`}
                                position="top"
                              >
                                <span>{customerRoute.totalCostFormatted}</span>
                              </Tooltip>
                            ) : (
                              <span className="text-gray-300 font-normal">
                                —
                              </span>
                            )}
                          </td>
                          {/* Your: vs Avg - hidden when SHOW_PLATFORM_METRICS is false */}
                          {SHOW_PLATFORM_METRICS && (
                            <td className="py-3 px-2 text-right tabular-nums">
                              {customerRoute &&
                              platformAvgPerCustomerDay > 0 ? (
                                <Tooltip
                                  text={`Your ${formatNumber(
                                    yourDailyCalls
                                  )}/day vs avg customer ${formatNumber(
                                    platformAvgPerCustomerDay
                                  )}/day`}
                                  position="left"
                                >
                                  <span
                                    className={`inline-flex items-center gap-1 ${
                                      vsAvgPct > 0
                                        ? "text-green-600"
                                        : vsAvgPct < 0
                                        ? "text-amber-600"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {vsAvgPct > 0 ? (
                                      <ArrowTrendingUpIcon className="h-3.5 w-3.5" />
                                    ) : vsAvgPct < 0 ? (
                                      <ArrowTrendingDownIcon className="h-3.5 w-3.5" />
                                    ) : (
                                      <MinusIcon className="h-3.5 w-3.5" />
                                    )}
                                    {vsAvgPct > 0 ? "+" : ""}
                                    {vsAvgPct.toFixed(0)}%
                                  </span>
                                </Tooltip>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          )}
                          {/* Model Override Selector */}
                          <td
                            className="py-2 px-2 border-l border-gray-100"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <select
                              value={pendingOverrides[route.routeKey] || ""}
                              onChange={(e) =>
                                handleModelChange(
                                  route.routeKey,
                                  e.target.value
                                )
                              }
                              className={`w-full text-xs py-1.5 px-2 rounded border ${
                                !modelOverridesUnlocked
                                  ? "border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed"
                                  : pendingOverrides[route.routeKey]
                                  ? "border-purple-300 bg-purple-50 text-purple-700"
                                  : "border-gray-200 bg-white text-gray-600"
                              } focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                              title={
                                modelOverridesUnlocked
                                  ? "Select AI model for this route"
                                  : modelOverridesLockedTooltip
                              }
                              disabled={!modelOverridesUnlocked}
                            >
                              <option value="">Passio Default</option>
                              {availableModels
                                .filter((m) => m.tag !== "no-ai")
                                .sort(
                                  (a, b) =>
                                    a.token_multiplier - b.token_multiplier
                                )
                                .map((model) => (
                                  <option key={model.tag} value={model.tag}>
                                    {model.name} (
                                    {model.token_multiplier.toFixed(1)}x)
                                  </option>
                                ))}
                            </select>
                          </td>
                        </tr>
                        {/* Expanded detail row */}
                        {isExpanded && (
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <td
                              colSpan={SHOW_PLATFORM_METRICS ? 11 : 7}
                              className="py-4 px-6"
                            >
                              <div
                                className={`grid ${
                                  SHOW_PLATFORM_METRICS
                                    ? "grid-cols-3"
                                    : "grid-cols-2"
                                } gap-6 text-xs`}
                              >
                                {/* Platform Metrics - hidden when SHOW_PLATFORM_METRICS is false */}
                                {SHOW_PLATFORM_METRICS && (
                                  <div className="space-y-3">
                                    <h5 className="font-semibold text-gray-700 border-b pb-1 flex items-center justify-between">
                                      Platform Metrics (All Customers)
                                      <a
                                        href={route.docsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800 font-normal inline-flex items-center gap-1"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        View Docs{" "}
                                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                      </a>
                                    </h5>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                      <div>
                                        <span className="text-gray-500">
                                          Total Calls:
                                        </span>
                                        <span className="ml-2 font-medium text-gray-900">
                                          {formatNumber(route.totalCalls)}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Total Tokens:
                                        </span>
                                        <span className="ml-2 font-medium text-gray-900">
                                          {route.totalTokensFormatted}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Calls/Day:
                                        </span>
                                        <span className="ml-2 font-medium text-gray-900">
                                          {formatNumber(route.avgDailyCalls)}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Calls/Hour:
                                        </span>
                                        <span className="ml-2 font-medium text-gray-900">
                                          {formatNumber(route.avgHourlyCalls)}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Avg/Customer/Day:
                                        </span>
                                        <span className="ml-2 font-medium text-gray-900">
                                          {formatNumber(
                                            platformAvgPerCustomerDay
                                          )}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Unique Customers:
                                        </span>
                                        <span className="ml-2 font-medium text-gray-900">
                                          {route.uniqueCustomers}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Your Usage Metrics */}
                                <div className="space-y-3">
                                  <h5 className="font-semibold text-indigo-700 border-b border-indigo-200 pb-1">
                                    Your Usage
                                  </h5>
                                  {customerRoute ? (
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                      <div>
                                        <span className="text-gray-500">
                                          Total Calls:
                                        </span>
                                        <span className="ml-2 font-medium text-indigo-600">
                                          {formatNumber(
                                            customerRoute.totalCalls
                                          )}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Total Tokens:
                                        </span>
                                        <span className="ml-2 font-medium text-indigo-600">
                                          {customerRoute.totalTokensFormatted}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Calls/Day:
                                        </span>
                                        <span className="ml-2 font-medium text-indigo-600">
                                          {formatNumber(
                                            customerRoute.avgDailyCalls
                                          )}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Calls/Hour:
                                        </span>
                                        <span className="ml-2 font-medium text-indigo-600">
                                          {formatNumber(
                                            customerRoute.avgHourlyCalls
                                          )}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Tokens/Day:
                                        </span>
                                        <span className="ml-2 font-medium text-indigo-600">
                                          {formatNumber(
                                            customerRoute.avgDailyTokens
                                          )}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">
                                          Tokens/Call:
                                        </span>
                                        <span className="ml-2 font-medium text-indigo-600">
                                          {formatNumber(
                                            customerRoute.avgTokensPerCall
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-gray-600 italic">
                                      No usage data for this route
                                    </p>
                                  )}
                                </div>

                                {/* NEW: Token Breakdown (v2 billing) */}
                                {USE_NEW_BILLING_DISPLAY &&
                                  customerRoute?.hasNewBillingData && (
                                    <div className="space-y-3">
                                      <h5 className="font-semibold text-amber-700 border-b border-amber-200 pb-1 flex items-center gap-2">
                                        <SparklesIcon className="h-4 w-4" />
                                        Token Breakdown
                                      </h5>
                                      <div className="space-y-3 text-sm">
                                        {/* Raw Tokens */}
                                        <div className="bg-slate-50 rounded-lg p-2">
                                          <p className="text-xs font-medium text-slate-600 mb-1">
                                            Raw Provider Tokens
                                          </p>
                                          <div className="flex gap-4">
                                            <div>
                                              <span className="text-slate-500 text-xs">
                                                In:
                                              </span>
                                              <span className="ml-1 font-medium text-slate-700">
                                                {formatNumber(
                                                  customerRoute.rawInputTokens ||
                                                    0
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className="text-slate-500 text-xs">
                                                Out:
                                              </span>
                                              <span className="ml-1 font-medium text-slate-700">
                                                {formatNumber(
                                                  customerRoute.rawOutputTokens ||
                                                    0
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Normalized Tokens */}
                                        <div className="bg-indigo-50 rounded-lg p-2">
                                          <p className="text-xs font-medium text-indigo-600 mb-1 flex items-center gap-1">
                                            Normalized
                                            <Tooltip
                                              text={
                                                "Adjusted to $1.50/M rate using multipliers:\n" +
                                                `Input: ${
                                                  customerRoute.inputPriceMultiplier?.toFixed(
                                                    4
                                                  ) || "N/A"
                                                }x\n` +
                                                `Output: ${
                                                  customerRoute.outputPriceMultiplier?.toFixed(
                                                    4
                                                  ) || "N/A"
                                                }x`
                                              }
                                              position="top"
                                            >
                                              <QuestionMarkCircleIcon className="h-3 w-3" />
                                            </Tooltip>
                                          </p>
                                          <div className="flex gap-4">
                                            <div>
                                              <span className="text-indigo-500 text-xs">
                                                In:
                                              </span>
                                              <span className="ml-1 font-medium text-indigo-700">
                                                {formatNumber(
                                                  customerRoute.convertedInputTokens ||
                                                    0
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className="text-indigo-500 text-xs">
                                                Out:
                                              </span>
                                              <span className="ml-1 font-medium text-indigo-700">
                                                {formatNumber(
                                                  customerRoute.convertedOutputTokens ||
                                                    0
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Billed Tokens */}
                                        <div className="bg-amber-50 rounded-lg p-2">
                                          <p className="text-xs font-medium text-amber-600 mb-1 flex items-center gap-1">
                                            Billed
                                            <span className="text-xs font-normal text-amber-500 bg-amber-100 px-1.5 py-0.5 rounded-full">
                                              +
                                              {(
                                                (customerRoute.inputMarkupRate ||
                                                  0.33) * 100
                                              ).toFixed(0)}
                                              %
                                            </span>
                                          </p>
                                          <div className="flex gap-4">
                                            <div>
                                              <span className="text-amber-500 text-xs">
                                                In:
                                              </span>
                                              <span className="ml-1 font-bold text-amber-700">
                                                {formatNumber(
                                                  customerRoute.billedInputTokens ||
                                                    0
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className="text-amber-500 text-xs">
                                                Out:
                                              </span>
                                              <span className="ml-1 font-bold text-amber-700">
                                                {formatNumber(
                                                  customerRoute.billedOutputTokens ||
                                                    0
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                          <p className="text-xs text-amber-600 mt-1 font-medium">
                                            Total:{" "}
                                            {formatNumber(
                                              customerRoute.billedTokensTotal ||
                                                0
                                            )}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                {/* Cost & Health Metrics */}
                                <div className="space-y-3">
                                  <h5 className="font-semibold text-green-700 border-b border-green-200 pb-1">
                                    Cost & Health
                                  </h5>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                    {customerRoute ? (
                                      <>
                                        {/* Show new cost breakdown if available */}
                                        {USE_NEW_BILLING_DISPLAY &&
                                        customerRoute.hasNewBillingData ? (
                                          <>
                                            <div className="col-span-2 bg-gradient-to-r from-slate-50 to-green-50 rounded-lg p-2 mb-1">
                                              <div className="flex justify-between items-center">
                                                <div>
                                                  <p className="text-xs text-slate-500">
                                                    Provider Cost
                                                  </p>
                                                  <p className="font-medium text-slate-600">
                                                    {formatCurrency(
                                                      customerRoute.wholesaleCostUSD ||
                                                        0
                                                    )}
                                                  </p>
                                                </div>
                                                <div className="text-center px-3">
                                                  <div className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full font-medium">
                                                    +
                                                    {(
                                                      (customerRoute.inputMarkupRate ||
                                                        0.33) * 100
                                                    ).toFixed(0)}
                                                    %
                                                  </div>
                                                </div>
                                                <div className="text-right">
                                                  <p className="text-xs text-green-600">
                                                    Your Cost
                                                  </p>
                                                  <p className="font-bold text-green-700">
                                                    {formatCurrency(
                                                      customerRoute.estimatedCostUSD ||
                                                        0
                                                    )}
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">
                                                Cost/Day:
                                              </span>
                                              <span className="ml-2 font-medium text-green-600">
                                                {formatCurrency(
                                                  customerRoute.avgDailyCostUSD
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">
                                                Cost/Call:
                                              </span>
                                              <span className="ml-2 font-medium text-green-600">
                                                {formatCurrency(
                                                  customerRoute.costPerCall
                                                )}
                                              </span>
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div>
                                              <span className="text-gray-500">
                                                Total Cost:
                                              </span>
                                              <span className="ml-2 font-medium text-green-600">
                                                {
                                                  customerRoute.totalCostFormatted
                                                }
                                              </span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">
                                                Cost/Day:
                                              </span>
                                              <span className="ml-2 font-medium text-green-600">
                                                {formatCurrency(
                                                  customerRoute.avgDailyCostUSD
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">
                                                Cost/Hour:
                                              </span>
                                              <span className="ml-2 font-medium text-green-600">
                                                {formatCurrency(
                                                  customerRoute.avgHourlyCostUSD
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">
                                                Cost/Call:
                                              </span>
                                              <span className="ml-2 font-medium text-green-600">
                                                {formatCurrency(
                                                  customerRoute.costPerCall
                                                )}
                                              </span>
                                            </div>
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <div className="col-span-2 text-gray-600 italic">
                                        No cost data
                                      </div>
                                    )}
                                    {hasUptimeData && (
                                      <>
                                        <div className="col-span-2 border-t pt-2 mt-1">
                                          <span className="text-gray-500">
                                            Platform Uptime:
                                          </span>
                                          <span
                                            className={`ml-2 font-medium ${
                                              uptimeRatePct >= 99
                                                ? "text-green-600"
                                                : uptimeRatePct >= 95
                                                ? "text-yellow-600"
                                                : "text-red-600"
                                            }`}
                                          >
                                            {uptimeRatePct.toFixed(2)}%
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">
                                            Success Calls:
                                          </span>
                                          <span className="ml-2 font-medium text-green-600">
                                            {formatNumber(
                                              route.uptimeSuccessCalls
                                            )}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">
                                            Error Calls:
                                          </span>
                                          <span
                                            className={`ml-2 font-medium ${
                                              route.uptimeErrorCalls > 0
                                                ? "text-red-600"
                                                : "text-gray-600"
                                            }`}
                                          >
                                            {formatNumber(
                                              route.uptimeErrorCalls
                                            )}
                                          </span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {showRoutesTable && (
          <div className="p-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex flex-wrap items-center gap-4">
            <span className="font-medium text-gray-600">
              {dateRangeStr} ({days} days)
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-600">Click row to open docs</span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-600">Click headers to sort</span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-600">
              &ldquo;Avg/Cust/Day&rdquo; = total calls ÷ days ÷ customers
            </span>
          </div>
        )}
      </Card>

      {/* Easy AI Promo */}
      <Card className="p-5 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border-indigo-200 relative overflow-hidden">
        <div className="absolute top-2 right-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowEasyAIPromo((prev) => !prev)}
            className="rounded-full bg-white/80 p-1 text-indigo-600 shadow-sm hover:bg-white"
            aria-label={
              showEasyAIPromo
                ? "Collapse Easy AI promo"
                : "Expand Easy AI promo"
            }
          >
            {showEasyAIPromo ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
          <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2.5 py-1 rounded-full animate-pulse shadow-lg">
            NEW
          </span>
        </div>
        {showEasyAIPromo && (
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl shadow-sm">
              <SparklesIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-xl text-indigo-900 mb-2">
                Don&apos;t see the AI function you need? Build it yourself!
              </h4>
              <p className="text-sm text-indigo-800 mb-3">
                Create unlimited custom AI functions tailored to your exact
                needs. No coding or ML expertise required - get production-ready
                API routes in seconds.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs">
                <div className="bg-white/60 rounded-md p-2 border border-indigo-100">
                  <p className="font-semibold text-indigo-800">Any Media</p>
                  <p className="text-gray-600">Images, audio, video, PDFs</p>
                </div>
                <div className="bg-white/60 rounded-md p-2 border border-indigo-100">
                  <p className="font-semibold text-indigo-800">
                    Custom Chatbots
                  </p>
                  <p className="text-gray-600">Your own knowledge bases</p>
                </div>
                <div className="bg-white/60 rounded-md p-2 border border-indigo-100">
                  <p className="font-semibold text-indigo-800">Any Model</p>
                  <p className="text-gray-600">GPT-4o, Claude, Gemini</p>
                </div>
                <div className="bg-white/60 rounded-md p-2 border border-indigo-100">
                  <p className="font-semibold text-indigo-800">Chain Steps</p>
                  <p className="text-gray-600">Pre-made template chains</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-indigo-600 font-medium">
                  Build custom AI routes for tools, business workflows,
                  chatbots, and RAG databases. You can still link outputs back
                  to Passio ingredients.
                </p>
                {onOpenEasyAI && (
                  <button
                    type="button"
                    onClick={onOpenEasyAI}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    Open Easy AI Builder
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Chart Section - Your Top Routes */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-900">
            Your Top API Routes by Usage
          </h4>
          <button
            type="button"
            onClick={() => setShowTopRoutesChart((prev) => !prev)}
            className="text-gray-500 hover:text-gray-700"
            aria-label={
              showTopRoutesChart
                ? "Collapse top routes chart"
                : "Expand top routes chart"
            }
          >
            {showTopRoutesChart ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        {showTopRoutesChart && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
                <p className="text-base font-semibold text-indigo-900">
                  Your avg calls
                </p>
                <p>
                  {formatNumber(customerAvgDailyCalls)} calls/day •{" "}
                  {formatNumber(customerAvgHourlyCalls)} calls/hr
                </p>
              </div>
              <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700">
                <p className="text-base font-semibold text-purple-900">
                  Your avg tokens
                </p>
                <p>
                  {formatNumber(customerAvgDailyTokens)} tokens/day •{" "}
                  {formatNumber(customerAvgHourlyTokens)} tokens/hr
                </p>
              </div>
            </div>
            {chartData.customerCalls.some((c) => c > 0) ? (
              <div className="flex justify-center">
                <Plot
                  data={[
                    {
                      type: "bar",
                      name: "Your Calls",
                      x: chartData.labelsShort,
                      hovertext: chartData.labelsFull,
                      customdata: chartData.customerAvgDailyCalls.map(
                        (avgDaily, idx) => [
                          avgDaily,
                          chartData.customerAvgHourlyCalls[idx],
                          chartData.customerTokens?.[idx] || 0,
                        ]
                      ),
                      hovertemplate:
                        "%{hovertext}<br>Total calls: %{y}<br>Avg/day: %{customdata[0]:.2f}<br>Avg/hr: %{customdata[1]:.2f}<extra></extra>",
                      y: chartData.customerCalls,
                      marker: {
                        color: chartData.colors,
                      },
                    },
                  ]}
                  layout={{
                    height: 320,
                    margin: { t: 30, b: 90, l: 60, r: 30 },
                    xaxis: {
                      tickangle: -40,
                      tickfont: { size: 10 },
                      automargin: true,
                    },
                    yaxis: {
                      title: "Your Calls",
                    },
                    hovermode: "x unified",
                    hoverlabel: { namelength: -1 },
                    paper_bgcolor: "transparent",
                    plot_bgcolor: "transparent",
                    showlegend: false,
                  }}
                  config={{ displayModeBar: false, responsive: true }}
                  style={{
                    width: "100%",
                    maxWidth: "1100px",
                    height: "320px",
                  }}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>
                  No usage data yet. Start making API calls to see your top
                  routes.
                </p>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default RouteInsights;
